import React, { useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import StatusBadge from "@/components/StatusBadge";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SparkleIcon, ArrowsClockwiseIcon, FileTextIcon, LockKeyIcon, LockKeyOpenIcon, CornersOutIcon, CornersInIcon } from "@phosphor-icons/react";
import { openTracePdf } from "@/lib/traceExport";
import GanttBarTooltip from "./GanttBarTooltip";
import { GanttBarLabel } from "./GanttBarLabel";

const selCls = "bg-[var(--bg-0)] border-[var(--border)] text-white h-9";

// ================================================================
// TAB 4 — Gantt Planning Board (read-only MVP; drag-drop follow-up)
// ================================================================
export default function GanttTab({ machines, orders, items, deliveryOnly = false }) {
  const { user, hasPerm } = useAuth();
  const isAdmin = (user?.role || "") === "Administrator";
  const canLock = isAdmin || hasPerm("planning:edit");  // FE-PERM-1b.1
  const today = new Date().toISOString().slice(0, 10);
  const in28 = new Date(Date.now() + 27 * 86400000).toISOString().slice(0, 10);
  const [from, setFrom] = useState(today);
  const [to, setTo] = useState(in28);
  const [plans, setPlans] = useState([]);
  // Sep 03, 2026 · Work Orders join · one hue per SO but we want the WO
  // number in front of the SO on every bar so shop floor sees the
  // canonical WO reference at a glance.
  const [workOrders, setWorkOrders] = useState([]);
  // PVA-MACHINE-PLAN-01 · unfiltered WOs, used only for Estimated Delivery WO qty lookup.
  const [allWorkOrders, setAllWorkOrders] = useState([]);
  const [typeFilter, setTypeFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  // Feb 2026 · Cursor-follow tooltip state. One shared instance mounted
  // at the tree bottom via portal — hovering any bar sets `{plan, x, y}`,
  // leaving clears it. Zero-lag because we don't wait on any Radix delay
  // machinery and don't spawn a per-bar tooltip subtree.
  const [hoverTip, setHoverTip] = useState(null);
  const [loading, setLoading] = useState(false);
  // Feb 2026 · Full-screen toggle. CSS-based (not the browser fullscreen API)
  // so dialogs + toasts still layer correctly. ESC exits fullscreen.
  const [fullscreen, setFullscreen] = useState(false);
  // Sep 03, 2026 · Compact toggle — planners with big monitors can
  // flip back to the roomier 66-px rows for chip legibility, while
  // the default stays at 40 px so twice as many looms fit on-screen.
  // Preference persisted per browser so operators don't re-toggle.
  const [compact, setCompact] = useState(() => {
    try { return localStorage.getItem("gantt.compact") !== "0"; }
    catch { return true; }
  });
  useEffect(() => {
    try { localStorage.setItem("gantt.compact", compact ? "1" : "0"); } catch {}
  }, [compact]);
  useEffect(() => {
    if (!fullscreen) return;
    const onKey = (e) => { if (e.key === "Escape") setFullscreen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [fullscreen]);
  // Machine Type masters — needed to map each machine's `machine_type_id`
  // to its heads count so the "Suggest Fastest Loom" scan and cross-machine
  // drops can use the same picks/cm × speed × heads throughput formula the
  // Planning Calculator uses.
  const [machineTypeMasters, setMachineTypeMasters] = useState([]);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/machine-types/");
        setMachineTypeMasters(Array.isArray(data) ? data : []);
      } catch {
        setMachineTypeMasters([]);
      }
    })();
  }, []);

  useEffect(() => {
    if (!selected) {
      setFastestPlanId(null);
      setFastestList([]);
    }
  }, [selected]);

  const load = async () => {
    setLoading(true);
    try {
      // Sep 03, 2026 · Fetch work_orders alongside plans so we can prefix
      // the bar label with the canonical WO number. One SO/ESO can spawn
      // multiple WOs (one per line), so the join key is `source_order_id
      // + item_id`, falling back to just `source_order_id` for legacy
      // plans that predate line-linked WOs.
      const [plansR, wosR] = await Promise.all([
        api.get("/production-plans/"),
        api.get("/work-orders/").catch(() => ({ data: [] })),
      ]);
      setPlans(plansR.data || []);
      // Feb 2026 · Only Approved Work Orders join the Gantt / planner
      // pool. Draft WOs are still awaiting management sign-off and must
      // not consume capacity or show up as coloured bars.
      setWorkOrders((wosR.data || []).filter(w => w.status === "Approved"));
      setAllWorkOrders(wosR.data || []);
      setBetterLoomHints(new Map());
    } catch { toast.error("Failed to load plans"); }
    setLoading(false);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const days = useMemo(() => {
    const out = []; const start = new Date(from); const end = new Date(to);
    const cur = new Date(start);
    while (cur <= end) { out.push(cur.toISOString().slice(0, 10)); cur.setDate(cur.getDate() + 1); }
    return out;
  }, [from, to]);

  const machineTypes = useMemo(() => {
    const s = new Set(machines.map(m => m.machine_type).filter(Boolean));
    return Array.from(s);
  }, [machines]);
  // Sep 03, 2026 · Ascending alphanumeric machine sort.
  //
  // 1. Type filter is applied first (unchanged from Feb 2026).
  // 2. Rows are then grouped by `machine_type` (Weaving / Warping /
  //    Jacquard etc.) so departments cluster visually.
  // 3. Within each group we sort by `code` using JS `localeCompare` with
  //    `numeric: true` — this is the natural "Loom 2 before Loom 10"
  //    behaviour operators expect, without any hand-rolled regex.
  // 4. Group ordering is itself alphabetised so the section headers
  //    also read A → Z (Jacquard → Rapier → Warping etc.), keeping the
  //    board stable across reloads.
  //
  // Runs in both windowed + fullscreen modes because both consume the
  // same `rowsMachines` array — no extra plumbing needed.
  // Feb 2026 · Hidden looms + strict-ascending sort (Dynamic Gantt Organization).
  //   * Sort is locked to natural-order ASCENDING by `code` — no more
  //     group-by-machine-type block that used to shuffle rows.
  //   * `hiddenLooms` is a Set of machine ids; the checkbox per row and
  //     the "Show All" button toggle it. Persisted to localStorage so a
  //     planner's chosen view survives reloads.
  //   * `visibleMachines` drives the header count so it updates the
  //     moment any row is toggled.
  const [hiddenLooms, setHiddenLooms] = useState(() => {
    try {
      const raw = localStorage.getItem("mes.gantt.hiddenLooms");
      if (raw) return new Set(JSON.parse(raw));
    } catch (_e) { /* ignore */ }
    return new Set();
  });
  useEffect(() => {
    try { localStorage.setItem("mes.gantt.hiddenLooms", JSON.stringify([...hiddenLooms])); } catch (_e) { /* ignore */ }
  }, [hiddenLooms]);
  const toggleLoomHidden = (mid) => setHiddenLooms(prev => {
    const next = new Set(prev);
    next.has(mid) ? next.delete(mid) : next.add(mid);
    return next;
  });

  const rowsMachines = useMemo(() => {
    const filtered = typeFilter === "all"
      ? machines
      : machines.filter(m => m.machine_type === typeFilter);
    // Strict natural-ascending by code — L2 before L10, no type grouping.
    const cmp = (a, b) =>
      String(a.code || "").localeCompare(String(b.code || ""), undefined, { numeric: true, sensitivity: "base" });
    const sorted = [...filtered].sort(cmp);
    return sorted.filter(m => !hiddenLooms.has(m.id));
  }, [machines, typeFilter, hiddenLooms]);

  const orderNo = (id) => orders.find(o => o.id === id)?.order_no || "—";

  // Sep 03, 2026 · WO No lookup for the plan bar label. One SO/ESO can
  // spawn multiple WOs (one per SO line), so the resolution walks in
  // priority order:
  //   1. WO with matching `source_order_id` + `item_id` on any WO item
  //   2. WO with matching `source_order_id` + top-level `item_id`
  //   3. WO with just matching `source_order_id`
  // Returns the canonical `wo_no` (or `po_no` fallback) or "" when the
  // plan has no matching WO — the bar label handles empty gracefully.
  const woNoByPlan = (p) => {
    if (!p || !p.order_id) return "";
    const bySrc = workOrders.filter(w => (w.source_order_id || "") === p.order_id);
    if (bySrc.length === 0) return "";
    // Feb 2026 · Canonical link — match plan.line_id against WO.source_line_id
    // (or WO.items[].line_id for multi-item WOs) FIRST. This fixes the
    // split-heads bug where two shards on the same loom carried different
    // line_ids but the item_id-only match fell through to `bySrc[0]` and
    // labelled both bars with the same WO number.
    const byLine = p.line_id
      ? bySrc.find(w =>
          w.source_line_id === p.line_id ||
          (Array.isArray(w.items) && w.items.some(it => it && it.line_id === p.line_id))
        )
      : null;
    const byItem = !byLine && p.item_id
      ? bySrc.find(w =>
          (w.item_id && w.item_id === p.item_id) ||
          (Array.isArray(w.items) && w.items.some(it => it && it.item_id === p.item_id))
        )
      : null;
    // Feb 2026 · Split-head WO disambiguation. When SO has multiple lines
    // (2 WOs · same source_order_id · different products) and the shard's
    // line_id / item_id fail to match either WO cleanly, resolve via the
    // plan's own `product_code` / `article_no` against `wo.item_code` and
    // `wo.items[].item_code`. Without this, both shards mislabel to
    // `bySrc[0]` (WO-0003) even though shard #2 belongs to WO-0004.
    const planCode = (p.product_code || p.article_no || "").trim();
    const byProduct = !byLine && !byItem && planCode
      ? bySrc.find(w =>
          (w.item_code && w.item_code === planCode) ||
          (Array.isArray(w.items) && w.items.some(it => it && (it.item_code === planCode || it.article_no === planCode)))
        )
      : null;
    // When SO has multiple WOs and NONE match by line/item/product, refuse
    // to guess — return empty so the bar shows only the SO number rather
    // than confidently mislabelling. Legacy single-WO SOs still fall back
    // to bySrc[0] safely.
    const chosen = byLine || byItem || byProduct || (bySrc.length === 1 ? bySrc[0] : null);
    if (!chosen) return "";
    return chosen.wo_no || chosen.po_no || "";
  };
  // Sep 04, 2026 · WO production_qty for a plan — parallel to `woNoByPlan`.
  // Bar labels stamp `planned / WOqty` so planners see cap side-by-side
  // with what's been allocated. Returns 0 when no WO is linked; the bar
  // renderer suppresses the "/ X" suffix in that case.
  const woQtyByPlan = (p) => {
    if (!p || !p.order_id) return 0;
    const bySrc = workOrders.filter(w => (w.source_order_id || "") === p.order_id);
    if (bySrc.length === 0) return 0;
    const byLine = p.line_id
      ? bySrc.find(w =>
          w.source_line_id === p.line_id ||
          (Array.isArray(w.items) && w.items.some(it => it && it.line_id === p.line_id))
        )
      : null;
    const byItem = !byLine && p.item_id
      ? bySrc.find(w =>
          (w.item_id && w.item_id === p.item_id) ||
          (Array.isArray(w.items) && w.items.some(it => it && it.item_id === p.item_id))
        )
      : null;
    // Feb 2026 · Split-head parity with woNoByPlan — resolve by product_code
    // when line/item joins miss, and refuse to guess when SO has multiple WOs.
    const planCode = (p.product_code || p.article_no || "").trim();
    const byProduct = !byLine && !byItem && planCode
      ? bySrc.find(w =>
          (w.item_code && w.item_code === planCode) ||
          (Array.isArray(w.items) && w.items.some(it => it && (it.item_code === planCode || it.article_no === planCode)))
        )
      : null;
    const chosen = byLine || byItem || byProduct || (bySrc.length === 1 ? bySrc[0] : null);
    if (!chosen) return 0;
    if (typeof chosen.production_qty === "number") return Number(chosen.production_qty) || 0;
    if (Array.isArray(chosen.items)) {
      return chosen.items
        .filter(it => it && (
          (p.line_id && it.line_id === p.line_id) ||
          (!p.line_id && p.item_id && it.item_id === p.item_id) ||
          (!p.line_id && !p.item_id)
        ))
        .reduce((s, it) => s + (Number(it.production_qty) || 0), 0);
    }
    return 0;
  };
  const itemLabel = (id, plan = null) => {
    // Feb 2026 · Source-of-Truth refactor — the bar detail dialog now
    // prefers the WO's item over the plan's raw item_id. Only falls
    // back to the plan's own item_id lookup when no WO is linked
    // (legacy plans predating the refactor).
    if (plan) {
      const wo = woForPlan(plan);
      const pcFromWo = productCodeFromWo(wo);
      if (pcFromWo) return pcFromWo;
    }
    const it = items.find(i => i.id === id);
    return it ? (it.description || it.code || "—") : "—";
  };

  // Colour by status per the spec
  const statusColour = (s) => {
    switch ((s || "").toLowerCase()) {
      case "running": return "bg-emerald-500/70 border-emerald-400";
      case "planned": return "bg-blue-500/60 border-blue-400";
      case "paused": return "bg-amber-500/60 border-amber-400";
      case "completed": return "bg-zinc-800 border-zinc-600";
      default: return "bg-blue-500/60 border-blue-400";
    }
  };

  // Back-to-back palette (Feb 2026): when a loom carries multiple Planned
  // jobs stacked chronologically (via "Queue after"), rotate the production
  // segment through a colour palette so queued/next jobs pop visually as
  // distinct from the primary. Running and Completed keep their status
  // colours (emerald / zinc); only Planned jobs rotate.
  const QUEUE_PALETTE = [
    { bar: "bg-blue-900/40",    fill: "bg-blue-500/70",    ring: "border-blue-400/40",    label: "Primary"  },
    { bar: "bg-purple-900/40",  fill: "bg-purple-500/70",  ring: "border-purple-400/40",  label: "Queue #2" },
    { bar: "bg-cyan-900/40",    fill: "bg-cyan-500/70",    ring: "border-cyan-400/40",    label: "Queue #3" },
    { bar: "bg-pink-900/40",    fill: "bg-pink-500/70",    ring: "border-pink-400/40",    label: "Queue #4" },
    { bar: "bg-teal-900/40",    fill: "bg-teal-500/70",    ring: "border-teal-400/40",    label: "Queue #5" },
    { bar: "bg-amber-900/40",   fill: "bg-amber-500/70",   ring: "border-amber-400/40",   label: "Queue #6" },
  ];
  // Precompute chronological chain index per plan per machine so back-to-back
  // rank is O(1) inside the render loop.
  const chainIndex = useMemo(() => {
    const idx = new Map();
    const byMachine = new Map();
    plans.forEach(p => {
      const arr = byMachine.get(p.machine_id) || [];
      arr.push(p);
      byMachine.set(p.machine_id, arr);
    });
    byMachine.forEach(arr => {
      arr.sort((a, b) => (a.planned_start || "").localeCompare(b.planned_start || ""));
      arr.forEach((p, i) => idx.set(p.id, i));
    });    return idx;
  }, [plans]);

  // Feb 2026 · Multi-SO Machine Stacking helpers.
  //
  // Mirrors backend `_base_article_of` (server.py L922): splits on the
  // first `/`, `-` or space and returns the leading token upper-cased.
  // Kept in-file (not lib/article.js) because it must byte-match the
  // backend rule that gates whether two overlapping plans can share a
  // loom. Deliberately different from `lib/article.js::baseArticle`
  // (which returns the 3rd `/`-token for the Article-Master short-code
  // column) — that helper answers a different question.
  const baseArticleForStack = (s) => {
    const str = String(s ?? "").trim();
    if (!str) return "";
    for (const sep of ["/", "-", " "]) {
      const idx = str.indexOf(sep);
      if (idx >= 0) return str.slice(0, idx).trim().toUpperCase();
    }
    return str.toUpperCase();
  };
  // Resolve a plan's base article — prefers a denormalised article_no /
  // product_code on the plan doc itself (some plans carry it inline),
  // falls back to the SO line matched by item_id, else first line.
  const baseArticleForPlan = (p) => {
    if (!p) return "";
    const inline = p.article_no || p.product_code;
    if (inline) return baseArticleForStack(inline);
    const so = orders.find(o => o.id === p.order_id);
    if (!so) return "";
    const soItems = Array.isArray(so.items) ? so.items : [];
    // Feb 2026 · Prefer line_id match — split-head shards on the same SO
    // share item_ids across different lines, so item_id-only lookup would
    // collapse them onto the first line and mis-label the bar.
    const li = (p.line_id && soItems.find(x => x.line_id === p.line_id))
      || (p.item_id && soItems.find(x => x.item_id === p.item_id))
      || soItems[0];
    if (!li) return "";
    return baseArticleForStack(li.article_no || li.product_code || "");
  };
  // Feb 2026 · Source-of-Truth refactor · WO-first product-code
  // resolution. Given a plan, walk to its WO record (via denormalised
  // `work_order_id` or `source_wo_no`) and read the WO's item_code
  // strictly. SO line lookup is a legacy fallback ONLY for pre-refactor
  // plans that lack `work_order_id`.
  const woForPlan = (p) => {
    if (!p) return null;
    if (p.work_order_id) {
      const w = workOrders.find(x => x.id === p.work_order_id);
      if (w) return w;
    }
    if (p.source_wo_no) {
      const w = workOrders.find(x => (x.wo_no || x.po_no) === p.source_wo_no);
      if (w) return w;
    }
    return null;
  };
  const productCodeFromWo = (wo) => {
    if (!wo) return "";
    if (wo.item_code) return wo.item_code;
    for (const it of (wo.items || [])) {
      const code = (it || {}).item_code || (it || {}).article_no;
      if (code) return code;
    }
    if (wo.item_id) {
      const it = items.find(x => x.id === wo.item_id);
      if (it) return it.code || it.article_no || it.description || "";
    }
    return "";
  };
  const productCodeForPlan = (p) => {
    if (!p) return "";
    // Feb 2026 · FIX — the plan's own `product_code` is the source of
    // truth, especially for split-head plans where two plans share a
    // WO but produce different variants. The previous version read the
    // WO's `item_code` first, which mislabelled the second shard on
    // NL-032 with shard-1's product code.
    const inline = p.product_code || p.article_no;
    if (inline) return inline;
    // Fall back to the WO's own field only when the plan doc doesn't
    // carry its own product_code (very old plans predating the
    // WO-centric `_enrich_plan_dna` refactor).
    const wo = woForPlan(p);
    const pcFromWo = productCodeFromWo(wo);
    if (pcFromWo) return pcFromWo;
    // Legacy last-resort — SO line lookup for plans that predate
    // both the WO field and the inline denorm.
    const so = orders.find(o => o.id === p.order_id);
    if (!so) return "";
    const soItems = Array.isArray(so.items) ? so.items : [];
    const li = (p.line_id && soItems.find(x => x.line_id === p.line_id))
      || (p.item_id && soItems.find(x => x.item_id === p.item_id))
      || soItems[0];
    if (!li) return "";
    return li.product_code || li.article_no || "";
  };
  // Stack index per plan — number of active plans (Planned/Running/Paused)
  // on the same loom whose windows overlap AND share the same base article.
  // Returns > 1 when the bar is part of a legitimate stacked pair. Rendered
  // as a ▤N chip on the bar so planners see overlays at a glance.
  const stackCountByPlan = useMemo(() => {
    const map = new Map();
    const byMachine = new Map();
    plans.forEach(p => {
      if (!(p.status === "Planned" || p.status === "Running" || p.status === "Paused")) return;
      const arr = byMachine.get(p.machine_id) || [];
      arr.push(p);
      byMachine.set(p.machine_id, arr);
    });
    byMachine.forEach(arr => {
      arr.forEach(p => {
        const pb = baseArticleForPlan(p);
        const ps = p.planned_start || "";
        const pf = p.planned_finish || "";
        let count = 1;
        arr.forEach(q => {
          if (q.id === p.id) return;
          const qs = q.planned_start || "";
          const qf = q.planned_finish || "";
          if (qf < ps || qs > pf) return; // no overlap
          // Head-split shards share a window by design — don't count them.
          if (+p.heads_used > 0 && +q.heads_used > 0 && ps === qs && pf === qf) return;
          if (baseArticleForPlan(q) !== pb || !pb) return;
          count += 1;
        });
        if (count > 1) map.set(p.id, count);
      });
    });
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plans, orders]);

  // Feb 2026 · Order colour hash — one SO/ESO = one colour across every loom
  // + every queue slot. Stable across renders because it's keyed on the
  // order id (never generated on the fly). Falls back to plan.id for
  // planless / stock plans so nothing renders colour-less.
  const orderColourIndex = useMemo(() => {
    const map = new Map();
    // Deterministic FNV-ish hash → palette slot, so RNW-26-27-00001 always
    // lands on the same colour no matter where it sits on the board.
    const hash = (s) => {
      let h = 2166136261;
      for (let i = 0; i < s.length; i += 1) {
        h ^= s.charCodeAt(i);
        h = (h * 16777619) >>> 0;
      }
      return h;
    };
    plans.forEach(p => {
      const key = String(p.order_id || p.source_order_id || p.id || "");
      map.set(p.id, hash(key) % QUEUE_PALETTE.length);
    });
    return map;
  }, [plans]);

  // Queue-chain stats (Feb 2026): count how many Planned back-to-back plans
  // sit at each chain-index and how many distinct looms are hosting each
  // level. Powers the Q2/Q3/… chips in the legend row.
  const queueChainStats = useMemo(() => {
    const perLevel = new Map();
    plans.forEach(p => {
      const ci = chainIndex.get(p.id) || 0;
      if (ci <= 0) return; // Primary is not a queue level
      if (p.status !== "Planned") return; // Only planned Q# jobs
      const s = perLevel.get(ci) || { plans: 0, looms: new Set() };
      s.plans += 1;
      s.looms.add(p.machine_id);
      perLevel.set(ci, s);
    });
    return Array.from(perLevel.entries())
      .sort((a, b) => a[0] - b[0])
      .map(([level, s]) => ({ level, plans: s.plans, looms: s.looms.size }));
  }, [plans, chainIndex]);

  // Estimated Delivery Date per SO+Product (Feb 2026): groups all active
  // plans by (order_id, item_id) → picks the latest planned_finish across
  // the group as the shop-floor promised-out date, then compares it with the
  // SO's `delivery_date` to flag On-Time / Delayed status.
  //
  // Per-line promised date resolver (Feb 2026 · bug fix). Buyers often set a
  // different delivery_date on individual SO line items (e.g. staggered
  // dispatches on the same order). Fall-back chain per plan:
  //   1. matched SO item's `delivery_date` (via line_id ↔ item_id ↔ article)
  //   2. SO header `delivery_date`
  //   3. ""
  const promisedForPlan = (plan) => {
    if (!plan) return "";
    const so = orders.find(o => o.id === plan.order_id);
    if (!so) return "";
    const items = Array.isArray(so.items) ? so.items : [];
    let line = null;
    if (plan.line_id) line = items.find(li => li.line_id === plan.line_id) || null;
    if (!line && plan.item_id) line = items.find(li => li.item_id === plan.item_id) || null;
    return (line && line.delivery_date) || so.delivery_date || "";
  };
  // Line-level context resolver (Feb 2026) — exposes the SO / line index /
  // article so tooltips can quote the exact source of the promised date.
  const lineContextForPlan = (plan) => {
    if (!plan) return null;
    const so = orders.find(o => o.id === plan.order_id);
    if (!so) return null;
    const items = Array.isArray(so.items) ? so.items : [];
    let idx = -1;
    if (plan.line_id) idx = items.findIndex(li => li.line_id === plan.line_id);
    if (idx < 0 && plan.item_id) idx = items.findIndex(li => li.item_id === plan.item_id);
    const line = idx >= 0 ? items[idx] : null;
    const promised = (line && line.delivery_date) || so.delivery_date || "";
    const source = (line && line.delivery_date)
      ? `SO-${so.order_no || so.id.slice(0,6)} line ${idx + 1}`
      : `SO-${so.order_no || so.id.slice(0,6)} header`;
    return {
      order_no: so.order_no || so.id.slice(0, 6),
      line_index: idx + 1,
      article: (line && line.article_no) || "",
      promised,
      source,
    };
  };
  const orderDeliveryById = useMemo(() => {
    const m = new Map();
    orders.forEach(o => { if (o.id) m.set(o.id, o.delivery_date || ""); });
    return m;
  }, [orders]);
  const deliveryEstimates = useMemo(() => {
    const groups = new Map();
    plans.forEach(p => {
      if (!p.order_id || !p.item_id) return;
      const key = `${p.order_id}::${p.item_id}`;
      const g = groups.get(key) || { order_id: p.order_id, item_id: p.item_id, plans: [], total_qty: 0, actual_qty: 0, latest_finish: "" };
      g.plans.push(p);
      g.total_qty += +p.planned_qty || 0;
      g.actual_qty += +p.actual_qty || 0;
      if (p.planned_finish && (!g.latest_finish || p.planned_finish > g.latest_finish)) g.latest_finish = p.planned_finish;
      groups.set(key, g);
    });
    return Array.from(groups.values()).map(g => {
      const order = orders.find(o => o.id === g.order_id);
      // Prefer the per-line delivery_date since buyers stagger dispatches.
      // Fall back to the SO header when the line has none.
      const soItems = Array.isArray(order?.items) ? order.items : [];
      const lineDate = (soItems.find(li => li.item_id === g.item_id) || {}).delivery_date;
      const promised = lineDate || order?.delivery_date || "";
      const delta = (promised && g.latest_finish) ? Math.round((new Date(g.latest_finish) - new Date(promised)) / 86400000) : null;
      const allDone = g.plans.length > 0 && g.plans.every(p => p.status === "Completed");
      // P17-UAT WO-PLANNING-DOC-IDENTITY · resolve the latest/current authoritative
      // Planning Document for THIS SO+Product group (latest plan_date, then plan_no
      // DESC) and surface ONLY its identity — qty aggregation is unchanged, so no
      // duplicate Planned/Completed/Balance is introduced.
      const rep = [...g.plans].sort((a, b) => {
        const ad = a.plan_date || "", bd = b.plan_date || "";
        if (ad && !bd) return -1;
        if (!ad && bd) return 1;
        if (ad !== bd) return ad < bd ? 1 : -1;
        const an = a.plan_no || "", bn = b.plan_no || "";
        return an < bn ? 1 : an > bn ? -1 : 0;
      })[0] || {};
      // PVA-MACHINE-PLAN-01 · WO Order Qty + Production Qty (incl. buffer) per distinct linked WO.
      const wos = [...new Map(g.plans.map(p => allWorkOrders.find(x => x.id === p.work_order_id)
        || allWorkOrders.find(x => p.source_wo_no && (x.wo_no || x.po_no) === p.source_wo_no)).filter(Boolean).map(w => [w.id, w])).values()];
      const woQty = wos.reduce((a, w) => {
        const li = (w.items || []).find(x => x && x.item_id === g.item_id);
        return {
          ord: a.ord + (+(li ? li.order_qty : w.ordered_qty) || 0),
          prod: a.prod + (+(li ? (li.total_qty ?? (+li.order_qty || 0) + (+li.extra_qty || 0)) : w.production_qty) || 0),
        };
      }, { ord: 0, prod: 0 });
      return {
        ...g,
        wo_order_qty: wos.length ? woQty.ord : null,
        wo_production_qty: wos.length ? woQty.prod : null,
        plan_no: rep.plan_no || "",
        plan_date: rep.plan_date || "",
        wo_no: rep.source_wo_no || "",
        customer_name: rep.customer_name || order?.customer_name || order?.buyer_name || "",
        product_code: rep.product_code || "",
        order_no: order?.order_no || "—",
        // Feb 2026 · flag so the delivery table can render an EXP chip next
        // to Export Sales Orders (matches the Planning Calculator chip).
        is_export: !!order?._is_export,
        promised_delivery: promised,
        estimated_delivery: g.latest_finish,
        delta_days: delta,
        on_time: delta !== null && delta <= 0,
        delayed: delta !== null && delta > 0,
        all_done: allDone,
        progress_pct: g.total_qty > 0 ? Math.min(100, (g.actual_qty / g.total_qty) * 100) : 0,
      };
    }).sort((a, b) => {
      // P17-UAT WO-PLANNING-DOC-IDENTITY · deterministic: latest Plan Date first,
      // then Plan Doc No. DESC; legacy rows (no plan_date) sink to the bottom.
      const ad = a.plan_date || "", bd = b.plan_date || "";
      if (ad && !bd) return -1;
      if (!ad && bd) return 1;
      if (ad !== bd) return ad < bd ? 1 : -1;
      const an = a.plan_no || "", bn = b.plan_no || "";
      return an < bn ? 1 : an > bn ? -1 : 0;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plans, orders, allWorkOrders]);

  // Head-Split shard layout (Feb 2026): plans that share the same machine +
  // start/finish window AND carry `heads_used > 0` are stacked vertically as
  // proportional slivers inside the loom row. Non-shard plans get the full
  // row height. Returns per-plan `{ yOffset, heightPct }` in [0..1].
  const shardLayout = useMemo(() => {
    const layout = new Map();
    const byMachine = new Map();
    plans.forEach(p => {
      const arr = byMachine.get(p.machine_id) || [];
      arr.push(p);
      byMachine.set(p.machine_id, arr);
    });
    byMachine.forEach(arr => {
      // Group plans by shared start+finish window (only those with heads_used).
      const groups = new Map();
      arr.forEach(p => {
        if (!(+p.heads_used > 0)) { layout.set(p.id, { yOffset: 0, heightPct: 1 }); return; }
        const key = `${p.planned_start}::${p.planned_finish}`;
        const g = groups.get(key) || [];
        g.push(p);
        groups.set(key, g);
      });
      groups.forEach(g => {
        // Sort deterministically so the same shard order shows every render.
        g.sort((a, b) => (a.item_id || "").localeCompare(b.item_id || ""));
        const totalHeads = g.reduce((s, p) => s + (+p.heads_used || 0), 0) || 1;
        let cum = 0;
        g.forEach(p => {
          const h = (+p.heads_used || 0) / totalHeads;
          layout.set(p.id, { yOffset: cum, heightPct: h });
          cum += h;
        });
      });
    });
    return layout;
  }, [plans]);

  // Drag-and-drop reschedule state (Feb 2026). Bars are draggable BOTH
  // horizontally (dxDays reschedule within same loom) AND vertically
  // (dyRows move to another loom). On release, a PUT patches
  // `planned_start`, `planned_finish` and `machine_id`. `hasMoved` gates
  // the click handler so a plain click still opens the detail dialog.
  const [dragState, setDragState] = useState(null);
  // Suggest-fastest-loom (Feb 2026). When the planner clicks "Suggest fastest
  // loom" in the selected-bar dialog, we scan every compatible loom (same
  // machine_type, idle-ish or already free of overlapping active plans),
  // compute the finish date on each using its own capacity, and rank them.
  // `pulseMachineId` gets set to the winner so the Gantt row pulses briefly.
  const [fastestPlanId, setFastestPlanId] = useState(null);
  const [fastestList, setFastestList] = useState([]);
  const [pulseMachineId, setPulseMachineId] = useState(null);
  // Better-loom hints (Feb 2026): a Map<planId, {targetCode, targetFinish,
  // daysSaved, dailyGain}> populated by the "Check Better Looms" toolbar
  // button. When a plan has a hint, its Gantt bar shows a tiny amber ▲ pip
  // so planners spot optimisation candidates passively without opening each
  // bar dialog. Cleared on plans reload.
  const [betterLoomHints, setBetterLoomHints] = useState(new Map());
  const [betterScanBusy, setBetterScanBusy] = useState(false);
  // Ref mirror so document-level mouse handlers always read the LATEST state
  // (React closures otherwise capture the initial dragState and never see the
  // planner's dxDays / hasMoved updates during the drag).
  const dragRef = useRef(null);
  useEffect(() => { dragRef.current = dragState; }, [dragState]);
  const shiftDate = (iso, days) => {
    if (!iso) return iso;
    const d = new Date(iso);
    d.setDate(d.getDate() + days);
    return d.toISOString().slice(0, 10);
  };
  // Per-machine daily output using the machine roster's own capacity_mtr_hr
  // (already whole-machine — see backend auto_allocate). Falls back to the
  // plan's stamped daily_target when the target machine has no capacity.
  const dailyForMachine = (m, fallback) => {
    if (!m) return +fallback || 0;
    const cap = +m.capacity_mtr_hr || 0;
    const eff = +m.efficiency_pct || +m.current_efficiency || 85;
    const daily = cap * 22 * (eff / 100);
    return daily > 0 ? daily : (+fallback || 0);
  };
  // Heads count from the Machine Type master (Feb 2026 — matches the
  // Planning Calculator's `headsForMachine`). Falls back to 0 when the
  // machine has no master link.
  const headsForMachine = (m) => {
    if (!m) return 0;
    const mt = machineTypeMasters.find(t => t.id === m.machine_type_id);
    return +(mt?.no_of_heads || 0);
  };
  // Feb 2026 · Machine-pinning. Once production entry has been recorded
  // for a plan (actual_qty > 0) or the plan is Running/Completed, the SO
  // is bound to that loom — cross-machine moves are blocked so the shop
  // floor doesn't lose the actuals-vs-planned linkage. Horizontal
  // reschedule (date shift) stays allowed.
  // Sep 05, 2026 · Dynamic Schedule Locking — machine-row Lock/Unlock.
  // Locks every active plan on the machine. Auto-locked plans (created
  // by /production-entries) stay locked until an Administrator unlocks.
  const machineHasActiveLock = (mid) =>
    plans.some(p => p.machine_id === mid && p.locked && (p.status === "Planned" || p.status === "Running"));
  const machineHasAutoLock = (mid) =>
    plans.some(p => p.machine_id === mid && p.locked && p.lock_source === "auto" && (p.status === "Planned" || p.status === "Running"));
  const toggleMachineLock = async (m) => {
    const locked = machineHasActiveLock(m.id);
    try {
      if (locked) {
        if (machineHasAutoLock(m.id) && !isAdmin) {
          toast.error("Auto-locked (In-Process) rows can only be unlocked by an Administrator.");
          return;
        }
        const { data } = await api.post(`/planning/machine/${m.id}/unlock`);
        toast.success(`${m.code} unlocked · ${data.unlocked_count} plan${data.unlocked_count === 1 ? "" : "s"}`);
      } else {
        const { data } = await api.post(`/planning/machine/${m.id}/lock`, { reason: `Manually locked from Gantt row · ${m.code}` });
        if (data.locked_count === 0) toast(`No active plans on ${m.code} to lock`);
        else toast.success(`${m.code} locked · ${data.locked_count} plan${data.locked_count === 1 ? "" : "s"}`);
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Lock toggle failed");
    }
  };
  const togglePlanLock = async (p) => {
    try {
      if (p.locked) {
        if (p.lock_source === "auto" && !isAdmin) {
          toast.error("Auto-locked bars need an Administrator to unlock.");
          return;
        }
        await api.post(`/production-plans/${p.id}/unlock`);
        toast.success("Plan unlocked");
      } else {
        await api.post(`/production-plans/${p.id}/lock`, { reason: "Locked from Gantt detail dialog" });
        toast.success("Plan locked");
      }
      setSelected(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Lock toggle failed");
    }
  };

  // Sep 05, 2026 · Live Capacity Re-balancing — Pull-Forward.
  // Row-level: bulk-nudge every Planned bar on the machine into the freed
  // gap. Plan-level: shift one Planned bar forward. Backend rejects locked
  // / non-Planned bars, so the button hides for them.
  const undoSnap = async (p) => {
    try {
      await api.post(`/production-plans/${p.id}/undo-snap`);
      toast.success("Snap undone · original finish restored");
      setSelected(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Undo-snap failed");
    }
  };

  const pullForwardMachine = async (m) => {
    try {
      const { data } = await api.post(`/production-plans/machine/${m.id}/pull-forward`);
      if (data.nudged_count === 0) toast(`${m.code} · nothing to pull forward`);
      else toast.success(`${m.code} · ${data.nudged_count} bar${data.nudged_count === 1 ? "" : "s"} pulled forward · saved ${data.saved_days_total}d`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Pull-forward failed");
    }
  };
  const pullForwardPlan = async (p) => {
    try {
      const { data } = await api.post(`/production-plans/${p.id}/pull-forward`);
      if (!data.nudged) toast(`No earlier slot available for this plan`);
      else toast.success(`Pulled forward · saved ${data.saved_days}d (${data.planned_start} → ${data.planned_finish})`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Pull-forward failed");
    }
  };

  const isPinnedPlan = (p) => {
    if (!p) return false;
    // Sep 05, 2026 · Dynamic Schedule Locking — explicit lock flag
    // (manual or auto) freezes the bar for drag / extend / cross-loom.
    if (p.locked) return true;
    if (+p.actual_qty > 0) return true;
    if (p.status === "Running" || p.status === "Completed") return true;
    return false;
  };
  // Feb 2026 · Hours-delta chip. On any successful cross-loom move, stash
  // { fromH, toH, expiresAt } keyed by plan id and render a small blue
  // chip on the bar for ~6s so the planner sees the saving without having
  // to hover the tooltip.
  const [hoursDeltaChips, setHoursDeltaChips] = useState(new Map());
  const flashHoursDelta = (planId, fromH, toH) => {
    setHoursDeltaChips(prev => {
      const next = new Map(prev);
      next.set(planId, { fromH, toH, expiresAt: Date.now() + 6000 });
      return next;
    });
    setTimeout(() => {
      setHoursDeltaChips(prev => {
        if (!prev.has(planId)) return prev;
        const next = new Map(prev);
        next.delete(planId);
        return next;
      });
    }, 6200);
  };
  // Product-aware daily output (Feb 2026): mirrors the Planning Calculator's
  // formula so the Suggest-Fastest-Loom scan and cross-machine drops rank by
  // TRUE per-loom throughput for the specific product being scheduled.
  //   perHrPerHead = (speedRpm ÷ picksPerCm) × 60/100
  //   effPerHrPerHead = perHrPerHead × (stdEff/100)
  //   dailyOutput = effPerHrPerHead × machineHeads × 22h
  // Falls back to the capacity-based `dailyForMachine` when the Product
  // Master lacks specs OR the machine has no heads on file.
  const productAwareDailyForMachine = (m, item, fallback, sourceMachine = null) => {
    if (!m) return +fallback || 0;
    // Product Master field: standard_picks_per_cm (renamed from
    // picks_per_cm on Feb 2026 to match the "standard_*" convention used
    // by every other spec column — speed, efficiency, waste, setup, etc.)
    const picks = +item?.standard_picks_per_cm || 0;
    const speed = +item?.standard_speed_rpm || 0;
    const stdEff = +item?.standard_efficiency_pct || 0;
    const heads = headsForMachine(m);
    // (1) Preferred: absolute product-aware formula
    //   dailyOutput = (speed ÷ picks) × 60/100 × (eff/100) × heads × 22h
    if (picks > 0 && speed > 0 && heads > 0) {
      const perHrPerHead = (speed / picks) * (60 / 100);
      const effPerHrPerHead = perHrPerHead * ((stdEff || 100) / 100);
      const daily = effPerHrPerHead * heads * 22;
      if (daily > 0) return daily;
    }
    // (2) Feb 2026 · Heads-ratio scaler — kicks in when picks/cm is missing
    //     but both machines report a head count on the Machine Type master.
    //     Preserves the user request: "10-head machine produces more than
    //     8-head, speed & picks being same". Scales the CURRENT plan's
    //     daily_target by targetHeads / sourceHeads so a 400 m/day plan on
    //     8-head becomes 500 m/day on 10-head (same speed / same product).
    if (sourceMachine) {
      const srcHeads = headsForMachine(sourceMachine);
      if (srcHeads > 0 && heads > 0 && +fallback > 0 && srcHeads !== heads) {
        return (+fallback) * (heads / srcHeads);
      }
    }
    // (3) Machine-capacity fallback (existing behaviour)
    return dailyForMachine(m, fallback);
  };
  const recomputeFinishOnMachine = (targetMachine, plannedQty, planStart, fallbackDaily, item = null, sourceMachine = null) => {
    const daily = productAwareDailyForMachine(targetMachine, item, fallbackDaily, sourceMachine);
    if (!(daily > 0) || !(plannedQty > 0) || !planStart) return planStart;
    const days = Math.max(1, Math.ceil(plannedQty / daily));
    return shiftDate(planStart, days - 1);
  };

  // Feb 2026 · projected-hours helper — computes LIVE production hours for a
  // plan on any given machine using the current heads / speed / picks-per-cm
  // triangle from the Product Master. Assumes 22 productive hours/day (matches
  // the daily-output formula used everywhere else on the board). Returns null
  // when we can't compute confidently (missing planned_qty or unresolvable
  // machine). Used by both the Gantt bar tooltip and the Bar Detail dialog
  // so a cross-machine reallocation instantly reflects the correct hours
  // without waiting for the backend PUT to complete.
  const projectedHoursOnMachine = (plan, machine, item = null, sourceMachine = null) => {
    if (!plan || !machine) return null;
    const qty = +plan.planned_qty || 0;
    if (!(qty > 0)) return null;
    const daily = productAwareDailyForMachine(machine, item, plan.daily_target, sourceMachine);
    if (!(daily > 0)) return null;
    const mPerHr = daily / 22;
    if (!(mPerHr > 0)) return null;
    const hours = qty / mPerHr;
    const heads = headsForMachine(machine);
    return {
      hours: Math.round(hours * 10) / 10,
      mPerHr: Math.round(mPerHr * 10) / 10,
      daily: Math.round(daily),
      heads,
      days: Math.max(1, Math.ceil(qty / daily)),
    };
  };
  useEffect(() => {
    if (!dragState) return;
    const onMove = (e) => {
      const st = dragRef.current;
      if (!st) return;
      const dx = e.clientX - st.initialX;
      const dy = e.clientY - st.initialY;
      // Feb 2026 · fully locked when the plan has production entries or
      // is Running / Completed — BOTH axes clamped to zero so the bar
      // can't slide horizontally OR vertically. Guard also short-circuits
      // `hasMoved` so the release handler treats it as a plain click.
      const pinned = st.pinned === true;
      const dxDays = pinned ? 0 : Math.round(dx / CELL_W);
      const dyRows = pinned ? 0 : Math.round(dy / ROW_H);
      const hasMoved = !pinned && (Math.abs(dx) > 4 || Math.abs(dy) > 4);
      setDragState(s => (s ? { ...s, dxDays, dyRows, hasMoved } : null));
    };
    const onUp = async () => {
      const s = dragRef.current;
      document.body.style.cursor = "";
      if (s && s.hasMoved && (s.dxDays !== 0 || s.dyRows !== 0)) {
        const p = plans.find(x => x.id === s.planId);
        if (p) {
          // Feb 2026 · fully locked plans reject BOTH horizontal date
          // shifts AND vertical machine changes. Toast once and bail.
          if (isPinnedPlan(p)) {
            toast.error("Plan locked · production already entered for this SO — no drag reschedule allowed.");
            setDragState(s2 => (s2 ? { ...s2, releasing: true } : null));
            setTimeout(() => setDragState(null), 50);
            return;
          }
          const newStart = shiftDate(p.planned_start, s.dxDays);
          const curIdx = rowsMachines.findIndex(rm => rm.id === p.machine_id);
          const targetIdx = Math.max(0, Math.min(rowsMachines.length - 1, curIdx + (s.dyRows || 0)));
          const targetMachine = rowsMachines[targetIdx] || null;
          const targetMachineId = targetMachine?.id || p.machine_id;
          const crossMachine = targetMachineId !== p.machine_id;
          // Feb 2026 · pinned plan → refuse the cross-machine leg entirely.
          // Even though onMove clamps dyRows to 0, guard here too in case a
          // future path (e.g. AI apply) tries to bypass the drag flow.
          if (crossMachine && isPinnedPlan(p)) {
            toast.error("Plan locked · production already entered — cross-loom move blocked.");
            setDragState(s2 => (s2 ? { ...s2, releasing: true } : null));
            setTimeout(() => setDragState(null), 50);
            return;
          }
          // Feb 2026 · pass sourceMachine so the heads-ratio scaler can kick
          // in when picks/cm is missing on the Product Master.
          const sourceMachine = machines.find(mm => mm.id === p.machine_id) || null;
          const item = items.find(it => it.id === p.item_id) || null;
          const newFinish = crossMachine
            ? recomputeFinishOnMachine(targetMachine, +p.planned_qty || 0, newStart, p.daily_target, item, sourceMachine)
            : shiftDate(p.planned_finish, s.dxDays);
          try {
            // Backend PUT expects the full plan document — send the current
            // record with only the two dates + machine_id patched so
            // validation doesn't reject required fields.
            const payload = {
              ...p,
              planned_start: newStart,
              planned_finish: newFinish,
              machine_id: targetMachineId,
              ...(crossMachine ? {
                daily_target: Math.round(productAwareDailyForMachine(targetMachine, item, p.daily_target, sourceMachine)),
                // Feb 2026 · also persist the recomputed production_hours so
                // the bar tooltip and Bar Detail dialog show the true hours
                // for the destination loom (e.g. 10000m ÷ 138.9 m/hr = 72h
                // on 10-head vs 96h on 8-head).
                ...((() => {
                  const pr = projectedHoursOnMachine(p, targetMachine, item, sourceMachine);
                  return pr ? { production_hours: pr.hours } : {};
                })()),
              } : {}),
            };
            delete payload.id;
            delete payload.created_at;
            delete payload.updated_at;
            await api.put(`/production-plans/${p.id}`, payload);
            // Feb 2026 · flash on-bar chip with the hours delta for
            // cross-loom moves so the planner sees the saving instantly.
            if (crossMachine) {
              const oldProj = projectedHoursOnMachine(p, sourceMachine, item);
              const newProj = projectedHoursOnMachine(p, targetMachine, item, sourceMachine);
              if (oldProj && newProj) flashHoursDelta(p.id, oldProj.hours, newProj.hours);
            }
            toast.success(crossMachine
              ? `Moved to ${targetMachine?.code || targetMachineId} · ${newStart} → ${newFinish}`
              : `Rescheduled to ${newStart} → ${newFinish}`);
            load();
          } catch (err) {
            const d = err?.response?.data?.detail;
            const msg = typeof d === "string" ? d : (Array.isArray(d) ? d.map(x => x.msg || JSON.stringify(x)).join(", ") : "Move blocked");
            toast.error(msg);
          }
        }
      }
      setDragState(s2 => (s2 ? { ...s2, releasing: true } : null));
      setTimeout(() => setDragState(null), 50);
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
    return () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dragState && dragState.planId]);

  // Feb 2026 · Multi-SO Machine Stacking · drop-preview incompatibility.
  //
  // While the operator drags a bar cross-loom, resolve whether the TARGET
  // loom already carries an active plan whose window overlaps the projected
  // drop AND has a DIFFERENT base article than the dragged bar. If so, the
  // backend will 400 on release (see server.py::_check_plan_overlap) — so
  // paint the drop-target row red and show an "INCOMPATIBLE ARTICLE" chip
  // BEFORE they release the mouse, saving the round-trip.
  const dropTargetIncompatible = useMemo(() => {
    if (!dragState || !dragState.hasMoved) return null;
    const p = plans.find(x => x.id === dragState.planId);
    if (!p) return null;
    const curIdx = rowsMachines.findIndex(rm => rm.id === p.machine_id);
    const targetIdx = Math.max(0, Math.min(rowsMachines.length - 1, curIdx + (dragState.dyRows || 0)));
    const targetMachine = rowsMachines[targetIdx] || null;
    if (!targetMachine || targetMachine.id === p.machine_id) return null; // same-loom drag → no stack check
    const item = items.find(it => it.id === p.item_id) || null;
    const newStart = shiftDate(p.planned_start, dragState.dxDays);
    const newFinish = recomputeFinishOnMachine(targetMachine, +p.planned_qty || 0, newStart, p.daily_target, item);
    const pb = baseArticleForPlan(p);
    // Find the first incompatible plan on the target loom — enough for the warning chip.
    const clash = plans.find(x => {
      if (x.id === p.id) return false;
      if (x.machine_id !== targetMachine.id) return false;
      if (!(x.status === "Planned" || x.status === "Running")) return false;
      const xs = x.planned_start || "";
      const xf = x.planned_finish || "";
      if (xf < newStart || xs > newFinish) return false;
      // Head-split pair — backend exempts this from the collision check.
      if (+x.heads_used > 0 && +p.heads_used > 0) return false;
      const xb = baseArticleForPlan(x);
      return xb && pb && xb !== pb;
    });
    if (!clash) return null;
    return { machineId: targetMachine.id, machineCode: targetMachine.code, ourArticle: pb, theirArticle: baseArticleForPlan(clash), clashOrderNo: orderNo(clash.order_id) };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dragState, plans, rowsMachines, orders, items]);

  // Which plans overlap the currently-dragged plan's projected window? These
  // get a red pulse ring while dragging so the planner sees the conflict
  // BEFORE releasing the mouse. Ignores same-window head-split shards
  // (they legitimately share the loom). Cross-machine drags check the
  // TARGET loom for overlaps.
  const overlapPlanIds = useMemo(() => {
    const s = new Set();
    if (!dragState || !dragState.hasMoved || (dragState.dxDays === 0 && dragState.dyRows === 0)) return s;
    const p = plans.find(x => x.id === dragState.planId);
    if (!p) return s;
    const curIdx = rowsMachines.findIndex(rm => rm.id === p.machine_id);
    const targetIdx = Math.max(0, Math.min(rowsMachines.length - 1, curIdx + (dragState.dyRows || 0)));
    const targetMachineId = rowsMachines[targetIdx]?.id || p.machine_id;
    const targetMachine = rowsMachines[targetIdx] || null;
    const newStart = shiftDate(p.planned_start, dragState.dxDays);
    const newFinish = (targetMachineId !== p.machine_id)
      ? recomputeFinishOnMachine(targetMachine, +p.planned_qty || 0, newStart, p.daily_target, items.find(it => it.id === p.item_id) || null)
      : shiftDate(p.planned_finish, dragState.dxDays);
    plans.forEach(x => {
      if (x.id === p.id) return;
      if (x.machine_id !== targetMachineId) return;
      if (!(x.status === "Planned" || x.status === "Running")) return;
      if (+x.heads_used > 0 && +p.heads_used > 0) return; // shards can share
      const xs = x.planned_start || "";
      const xf = x.planned_finish || "";
      const overlap = !(xf < newStart || xs > newFinish);
      if (overlap) { s.add(p.id); s.add(x.id); }
    });
    return s;
  }, [dragState, plans, rowsMachines]);

  // Suggest-fastest-loom scan (Feb 2026): for a given plan, evaluate every
  // compatible loom (same machine_type; falls back to any machine when
  // machine_type is missing), compute the projected finish using
  // `dailyForMachine`, skip looms with overlapping active plans in the
  // proposed window, and return the top 5 sorted by earliest finish.
  const _overlapsActiveIn = (machineId, startISO, finishISO, excludePlanId) => {
    return plans.some(x => {
      if (excludePlanId && x.id === excludePlanId) return false;
      if (x.machine_id !== machineId) return false;
      if (!(x.status === "Planned" || x.status === "Running")) return false;
      const xs = x.planned_start || "";
      const xf = x.planned_finish || "";
      return !(xf < startISO || xs > finishISO);
    });
  };
  const scanFastestLooms = (plan) => {
    if (!plan) return [];
    const qty = +plan.planned_qty || 0;
    const start = plan.planned_start;
    if (!qty || !start) return [];
    const src = machines.find(m => m.id === plan.machine_id);
    const mType = src?.machine_type;
    const item = items.find(it => it.id === plan.item_id) || null;
    const pool = machines.filter(m =>
      m.id !== plan.machine_id &&
      (!mType || m.machine_type === mType) &&
      (m.status !== "Breakdown" && m.status !== "Maintenance")
    );
    const rows = pool.map(m => {
      // Feb 2026 · pass the SOURCE loom so the heads-ratio scaler kicks in
      // when picks/cm is missing on the Product Master (jacquard case).
      const finish = recomputeFinishOnMachine(m, qty, start, plan.daily_target, item, src);
      const daily = productAwareDailyForMachine(m, item, plan.daily_target, src);
      const heads = headsForMachine(m);
      const productAware = item && (+item.standard_picks_per_cm > 0) && (+item.standard_speed_rpm > 0) && heads > 0;
      const conflicts = _overlapsActiveIn(m.id, start, finish, plan.id);
      const days = finish && start ? Math.max(1, Math.round((new Date(finish) - new Date(start)) / 86400000) + 1) : 0;
      return { machine: m, finish, days, conflicts, daily: Math.round(daily), heads, productAware };
    }).filter(r => r.finish && !r.conflicts);
    rows.sort((a, b) => (a.finish || "").localeCompare(b.finish || ""));
    return rows.slice(0, 5);
  };
  const runSuggestFastest = (plan) => {
    const list = scanFastestLooms(plan);
    setFastestPlanId(plan?.id || null);
    setFastestList(list);
    if (list.length > 0) {
      setPulseMachineId(list[0].machine.id);
      // Auto-clear the pulse after ~6s so it stays visible but not permanent.
      setTimeout(() => setPulseMachineId((cur) => (cur === list[0].machine.id ? null : cur)), 6000);
    } else {
      setPulseMachineId(null);
      toast.warning("No faster compatible loom found");
    }
  };
  // Batch "Check Better Looms" scan — runs `scanFastestLooms` for every
  // active Planned/Running plan, keeps only cases where the top candidate
  // finishes earlier than the plan's current finish, and stashes hints on
  // the bars via `betterLoomHints`. Toolbar button + amber ▲ pip renderer.
  const runBetterLoomScan = async () => {
    setBetterScanBusy(true);
    const next = new Map();
    for (const p of plans) {
      if (!(p.status === "Planned" || p.status === "Running")) continue;
      const list = scanFastestLooms(p);
      if (!list.length) continue;
      const best = list[0];
      const cur = p.planned_finish || "";
      if (!cur || best.finish >= cur) continue;
      const daysSaved = Math.round((new Date(cur) - new Date(best.finish)) / 86400000);
      if (daysSaved <= 0) continue;
      next.set(p.id, {
        targetCode: best.machine.code,
        targetMachineId: best.machine.id,
        targetFinish: best.finish,
        daysSaved,
      });
    }
    setBetterLoomHints(next);
    setBetterScanBusy(false);
    if (next.size === 0) {
      toast.info("All active plans are already on their fastest loom");
    } else {
      toast.success(`Found ${next.size} plan${next.size === 1 ? "" : "s"} with a faster loom option`);
    }
  };

  // AI schedule-suggest wiring (Feb 2026) — POSTs the current MES snapshot
  // to /api/ai/schedule-suggest and renders the model's markdown-ish reply
  // in a dialog. Read-only recommendations, no writes.
  const [aiOpen, setAiOpen] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiAnswer, setAiAnswer] = useState("");
  const [aiMeta, setAiMeta] = useState(null);
  const runAiSchedule = async () => {
    setAiOpen(true);
    setAiBusy(true);
    setAiAnswer("");
    setAiMeta(null);
    try {
      const { data } = await api.post("/ai/schedule-suggest", { horizon_days: 30 });
      setAiAnswer(data?.answer || "");
      setAiMeta(data?.context_size || null);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "AI request failed");
    }
    setAiBusy(false);
  };

  // Parse Claude's plain-text answer for actionable "Move <order_no> to <machine_code>"
  // patterns (Feb 2026). Renders as one-click Apply buttons below the answer so
  // the read-only advisor becomes an actionable copilot. Regex tolerates common
  // phrasings: "Move X to Y", "X → Y", "X to loom Y", "reallocate X to Y", etc.
  // For each detected pair we resolve the SO by order_no, pick its FIRST active
  // Planned/Running plan, resolve the target machine by code, and Apply routes
  // to the same PUT the drag-drop cross-machine flow uses.
  const parsedActions = useMemo(() => {
    if (!aiAnswer) return [];
    const soRegex = /\bSO-\d{2}-\d{2}-\d{4,6}\b/g;
    const mcRegex = /\b(?:NL|JL|LM|LOOM|ML)-\d{2,4}\b/gi;
    const lines = aiAnswer.split(/\n+/);
    const found = [];
    const seen = new Set();
    for (const raw of lines) {
      const line = raw.trim();
      if (!line || line.length < 6) continue;
      const sos = line.match(soRegex) || [];
      const mcs = line.match(mcRegex) || [];
      if (!sos.length || !mcs.length) continue;
      // Pair each SO with the nearest machine code in the same line.
      for (const so of sos) {
        for (const mc of mcs) {
          const key = `${so}|${mc.toUpperCase()}`;
          if (seen.has(key)) continue;
          const order = orders.find(o => (o.order_no || "").toUpperCase() === so.toUpperCase());
          const target = machines.find(m => (m.code || "").toUpperCase() === mc.toUpperCase());
          if (!order || !target) continue;
          const activePlans = plans.filter(p => p.order_id === order.id && (p.status === "Planned" || p.status === "Running"));
          if (!activePlans.length) continue;
          // Pick the earliest active plan not already on the target loom.
          const plan = activePlans.filter(p => p.machine_id !== target.id).sort((a, b) => (a.planned_start || "").localeCompare(b.planned_start || ""))[0];
          if (!plan) continue;
          seen.add(key);
          found.push({ key, sourceLine: line.slice(0, 140), orderNo: so, order, machineCode: mc.toUpperCase(), target, plan });
        }
      }
    }
    return found;
  }, [aiAnswer, orders, machines, plans]);

  const [applyingKey, setApplyingKey] = useState(null);
  const [appliedKeys, setAppliedKeys] = useState(new Set());
  const applyAiAction = async (action) => {
    setApplyingKey(action.key);
    try {
      await applyFastestMove(action.plan, action.target);
      setAppliedKeys(prev => new Set(prev).add(action.key));
    } finally {
      setApplyingKey(null);
    }
  };
  // Wipe per-run state whenever a fresh AI answer arrives.
  useEffect(() => { setAppliedKeys(new Set()); }, [aiAnswer]);

  const applyFastestMove = async (plan, target) => {
    if (!plan || !target) return;
    // Feb 2026 · pinned plans cannot be moved cross-loom (production
    // entries already recorded against the source loom).
    if (isPinnedPlan(plan)) {
      toast.error("Machine locked · production already entered for this SO");
      return;
    }
    const newStart = plan.planned_start;
    const item = items.find(it => it.id === plan.item_id) || null;
    const sourceMachine = machines.find(mm => mm.id === plan.machine_id) || null;
    const newFinish = recomputeFinishOnMachine(target, +plan.planned_qty || 0, newStart, plan.daily_target, item, sourceMachine);
    try {
      const payload = {
        ...plan,
        planned_start: newStart,
        planned_finish: newFinish,
        machine_id: target.id,
        daily_target: Math.round(productAwareDailyForMachine(target, item, plan.daily_target, sourceMachine)),
      };
      // Feb 2026 · stamp recomputed production hours on cross-machine moves
      // driven by the Suggest-Fastest-Loom / AI-apply paths so the bar
      // tooltip shows the new loom's hours immediately.
      const pr = projectedHoursOnMachine(plan, target, item, sourceMachine);
      if (pr) payload.production_hours = pr.hours;
      delete payload.id;
      delete payload.created_at;
      delete payload.updated_at;
      await api.put(`/production-plans/${plan.id}`, payload);
      // Flash the on-bar hours delta so the planner sees the saving even
      // when the move came from Suggest-Fastest / AI-apply.
      const oldProj = projectedHoursOnMachine(plan, sourceMachine, item);
      const newProj = projectedHoursOnMachine(plan, target, item, sourceMachine);
      if (oldProj && newProj) flashHoursDelta(plan.id, oldProj.hours, newProj.hours);
      toast.success(`Moved to ${target.code} · ${newStart} → ${newFinish}`);
      setSelected(null);
      setFastestList([]);
      setFastestPlanId(null);
      load();
    } catch (err) {
      const d = err?.response?.data?.detail;
      const msg = typeof d === "string" ? d : (Array.isArray(d) ? d.map(x => x.msg || JSON.stringify(x)).join(", ") : "Move blocked");
      toast.error(msg);
    }
  };


  // Compute grid positioning
  // Feb 2026 · Fullscreen mode enlarges day cells (40→64px) and loom rows
  // so the board uses the extra screen real-estate for bigger bars, more
  // legible qty/order chips, and easier drag targets. Every downstream
  // layout calc (bar left/width, drop targets, header cells) reads these
  // two constants — no other math needs to change.
  //
  // Sep 03, 2026 · Compact toggle folded in — normal mode picks 40 px
  // (compact) or 66 px (roomy) so planners can trade fitted looms for
  // bar legibility without leaving the page.
  const CELL_W = fullscreen ? 64 : 40; // px per day
  // Sep 03, 2026 · Fullscreen "no-zoom" rule — user explicitly wants
  // MORE looms, not BIGGER looms, when they hit fullscreen. So the row
  // height is driven purely by the Compact toggle regardless of
  // fullscreen state. Day width still expands in fullscreen because
  // wider bars help spot rescheduling opportunities on multi-month
  // views without adding vertical clutter.
  const ROW_H = compact ? 40 : 66;  // px per loom row
  const totalWidth = days.length * CELL_W;

  const barFor = (p) => {
    const s = p.planned_start; const f = p.planned_finish;
    if (!s || !f || !days.length) return null;
    if (f < days[0] || s > days[days.length - 1]) return null;
    const startIdx = Math.max(0, days.indexOf(s));
    const endIdx = Math.min(days.length - 1, days.indexOf(f) < 0 ? (f > days[days.length - 1] ? days.length - 1 : 0) : days.indexOf(f));
    // Handle cases where date is outside [from, to] but overlaps
    const realStart = s < days[0] ? 0 : days.indexOf(s);
    const realEnd = f > days[days.length - 1] ? days.length - 1 : days.indexOf(f);
    if (realStart < 0 && realEnd < 0) return null;
    const left = Math.max(0, realStart) * CELL_W;
    const right = (Math.max(0, realEnd) + 1) * CELL_W;
    return { left, width: Math.max(CELL_W * 0.7, right - left) };
  };

  return (
    <div className={fullscreen ? "fixed inset-0 z-[100] bg-[var(--bg-0)] p-2 overflow-hidden flex flex-col gap-2" : "space-y-3"} data-testid="gantt-root">
      {/* Feb 2026 · `deliveryOnly` variant renders ONLY the Estimated
          Delivery per SO + Product table on its own tab so planners get a
          full-width breakdown without the loom calendar competing for
          screen real-estate. All the calendar plumbing above still runs so
          `deliveryEstimates` stays reactive to plan changes. */}
      {deliveryOnly && (
        <div className="tactical-card p-4 flex flex-wrap items-center gap-3">
          <SparkleIcon size={20} className="text-[var(--accent)]" weight="bold" />
          <div className="flex-1 min-w-64">
            <div className="overline">Estimated Delivery per SO + Product</div>
            <div className="text-xs text-zinc-500">Latest <span className="font-mono">planned_finish</span> across all shop-floor plans, compared with the SO's promised delivery date.</div>
          </div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">From</div>
          <input data-testid="gantt-from" type="date" value={from} onChange={e => setFrom(e.target.value)} className="h-8 bg-[var(--bg-0)] border border-[var(--border)] px-2 rounded-sm text-xs focus:outline-none focus:border-[var(--accent)]" />
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">To</div>
          <input data-testid="gantt-to" type="date" value={to} onChange={e => setTo(e.target.value)} className="h-8 bg-[var(--bg-0)] border border-[var(--border)] px-2 rounded-sm text-xs focus:outline-none focus:border-[var(--accent)]" />
          <button onClick={load} className="h-8 px-3 border border-[var(--border)] hover:bg-[var(--bg-2)] text-xs rounded-sm flex items-center gap-1.5">
            <ArrowsClockwiseIcon size={12} weight="bold" />Refresh
          </button>
          {/* Sep 03, 2026 · Compact toggle · fullscreen bar */}
          <button
            data-testid="gantt-compact-toggle"
            onClick={() => setCompact(v => !v)}
            className={`h-8 px-3 border text-xs rounded-sm flex items-center gap-1.5 ${compact ? "border-[var(--accent)]/60 text-[var(--accent)] hover:bg-[var(--accent)]/10" : "border-[var(--border)] hover:bg-[var(--bg-2)]"}`}
            title={compact ? "Compact rows (40 px) · click for roomy 66 px" : "Roomy rows (66 px) · click for compact 40 px"}
          >
            {compact ? "▤ Compact" : "▥ Roomy"}
          </button>
          <button
            data-testid="gantt-fullscreen-toggle"
            onClick={() => setFullscreen(v => !v)}
            className={`h-8 px-3 border text-xs rounded-sm flex items-center gap-1.5 ${fullscreen ? "border-amber-400/60 text-amber-300 hover:bg-amber-500/10" : "border-[var(--border)] hover:bg-[var(--bg-2)]"}`}
            title={fullscreen ? "Exit fullscreen (Esc)" : "Fullscreen"}
          >
            {fullscreen ? <><CornersInIcon size={12} weight="bold" />Exit Fullscreen</> : <><CornersOutIcon size={12} weight="bold" />Fullscreen</>}
          </button>
        </div>
      )}
      {!deliveryOnly && (<>
      {/* Sep 03, 2026 · Slim header — everything on a single row so
          the Gantt grid claims the largest possible vertical share.
          Breadcrumb + controls share one thin bar; the wordy help
          paragraph collapsed into a `?` tooltip on the title. */}
      <div className="tactical-card px-3 py-2 flex flex-wrap items-center gap-2 text-xs">
        <SparkleIcon size={16} className="text-[var(--accent)]" weight="bold" />
        <span className="font-mono uppercase tracking-widest text-zinc-300">Gantt Planning</span>
        <span className="text-zinc-600 hidden md:inline">·</span>
        <span className="text-[10px] text-zinc-500 hidden md:inline">Rows = looms · bars = plans · drag to reschedule</span>
        <span
          data-testid="gantt-help-tip"
          title="Rows = looms · Bars = production plans coloured by status · Drag horizontally to reschedule · Drag vertically to move to another loom · Click a bar for details."
          className="inline-flex items-center justify-center w-4 h-4 rounded-full border border-zinc-600 text-[9px] text-zinc-400 cursor-help hover:border-[var(--accent)] hover:text-[var(--accent)]"
        >?</span>

        <div className="flex-1 min-w-4" />

        <span className="text-[10px] uppercase tracking-widest text-zinc-500">From</span>
        <input data-testid="gantt-from" type="date" value={from} onChange={e => setFrom(e.target.value)} className="h-7 bg-[var(--bg-0)] border border-[var(--border)] px-2 rounded-sm text-xs focus:outline-none focus:border-[var(--accent)]" />
        <span className="text-[10px] uppercase tracking-widest text-zinc-500">To</span>
        <input data-testid="gantt-to" type="date" value={to} onChange={e => setTo(e.target.value)} className="h-7 bg-[var(--bg-0)] border border-[var(--border)] px-2 rounded-sm text-xs focus:outline-none focus:border-[var(--accent)]" />
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger data-testid="gantt-type-filter" className="bg-[var(--bg-0)] border-[var(--border)] h-7 text-xs w-36"><SelectValue placeholder="All Types" /></SelectTrigger>
          <SelectContent className="bg-[var(--bg-1)] border-[var(--border)]">
            <SelectItem value="all">All Machine Types</SelectItem>
            {machineTypes.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
          </SelectContent>
        </Select>
        <button onClick={load} className="h-7 px-2 border border-[var(--border)] hover:bg-[var(--bg-2)] text-xs rounded-sm flex items-center gap-1">
          <ArrowsClockwiseIcon size={11} weight="bold" />Refresh
        </button>
        <button
          data-testid="gantt-compact-toggle"
          onClick={() => setCompact(v => !v)}
          className={`h-7 px-2 border text-xs rounded-sm flex items-center gap-1 ${compact ? "border-[var(--accent)]/60 text-[var(--accent)] hover:bg-[var(--accent)]/10" : "border-[var(--border)] hover:bg-[var(--bg-2)]"}`}
          title={compact ? "Compact rows (40 px) · click for roomy 66 px" : "Roomy rows (66 px) · click for compact 40 px"}
        >
          {compact ? "▤ Compact" : "▥ Roomy"}
        </button>
        <button
          data-testid="gantt-fullscreen-toggle"
          onClick={() => setFullscreen(v => !v)}
          className={`h-7 px-2 border text-xs rounded-sm flex items-center gap-1 ${fullscreen ? "border-amber-400/60 text-amber-300 hover:bg-amber-500/10" : "border-[var(--border)] hover:bg-[var(--bg-2)]"}`}
          title={fullscreen ? "Exit fullscreen (Esc)" : "Fullscreen"}
        >
          {fullscreen ? <><CornersInIcon size={11} weight="bold" />Exit</> : <><CornersOutIcon size={11} weight="bold" />Fullscreen</>}
        </button>
      </div>

      {/* Sep 03, 2026 · Legend condensed to a single wrap-friendly row —
          dots shrunk to 8×8 px, gap-2. Auto-wraps on narrow screens but
          stays one line on desktop so the Gantt grid keeps its space. */}
      <div className="flex items-center gap-2 text-[10px] text-zinc-500 px-1 flex-wrap leading-tight">
        <span className="uppercase tracking-widest text-zinc-600">Legend</span>
        <span className="inline-flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-sm bg-yellow-500/70 border border-yellow-300/40"></span>Changeover</span>
        <span className="inline-flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-sm bg-orange-500/70 border border-orange-300/40"></span>Setup</span>
        <span className="inline-flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-sm bg-emerald-500/70 border border-emerald-300/40"></span>Progress</span>
        <span className="inline-flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-sm ring-2 ring-red-500 animate-pulse"></span>Conflict</span>
        <span className="inline-flex items-center gap-1" data-testid="gantt-legend-risk"><span className="inline-block w-2 h-2 rounded-sm ring-2 ring-amber-400/80"></span>Risk</span>
        <span className="inline-flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-sm bg-purple-500/40 border border-purple-400/40"></span>Head-split</span>
        <span className="text-zinc-600">·</span>
        <span>Production:</span>
        {[
          ["Planned", "bg-blue-500/70 border-blue-400"],
          ["Running", "bg-emerald-500/70 border-emerald-400"],
          ["Paused", "bg-amber-500/60 border-amber-400"],
          ["Completed", "bg-zinc-600/60 border-zinc-500"],
        ].map(([label, cls]) => (
          <span key={label} className="inline-flex items-center gap-1">
            <span className={`inline-block w-2 h-2 rounded-sm border ${cls}`}></span>{label}
          </span>
        ))}
        {queueChainStats.length > 0 && (
          <>
            <span className="text-zinc-600">·</span>
            <span className="uppercase tracking-widest text-zinc-600">Queue</span>
            <span className="inline-flex items-center gap-1" data-testid="gantt-queue-legend-primary">
              <span className={`inline-block w-2 h-2 rounded-sm ${QUEUE_PALETTE[0].fill} border ${QUEUE_PALETTE[0].ring}`}></span>Primary
            </span>
            {queueChainStats.map(({ level, plans: pn, looms }) => {
              const p = QUEUE_PALETTE[level % QUEUE_PALETTE.length];
              return (
                <span key={level} data-testid={`gantt-queue-legend-Q${level + 1}`}
                  className="inline-flex items-center gap-1"
                  title={`${pn} plan${pn === 1 ? "" : "s"} at Q${level + 1} across ${looms} loom${looms === 1 ? "" : "s"}`}>
                  <span className={`inline-block w-2 h-2 rounded-sm ${p.fill} border ${p.ring}`}></span>Q{level + 1}
                </span>
              );
            })}
          </>
        )}
        <span className="ml-auto text-zinc-500" data-testid="gantt-drag-hint">{plans.length} plan{plans.length === 1 ? "" : "s"} · {rowsMachines.length} loom{rowsMachines.length === 1 ? "" : "s"}{hiddenLooms.size > 0 ? ` · ${hiddenLooms.size} hidden` : ""} · drag to reschedule</span>
        {hiddenLooms.size > 0 && (
          <button
            type="button"
            data-testid="gantt-show-all-looms"
            onClick={() => setHiddenLooms(new Set())}
            title="Restore every hidden loom in one click"
            className="ml-2 h-7 px-2.5 text-[10px] font-semibold uppercase tracking-widest border border-[var(--accent)]/60 text-[var(--accent)] hover:bg-[var(--accent)]/10 rounded-sm transition"
          >
            Show All ({hiddenLooms.size})
          </button>
        )}
      </div>

      {/* Sep 03, 2026 · Grid claims max vertical space. In fullscreen
          it grows to 100vh minus the slim header + legend (~90 px);
          in windowed mode it targets 80vh so the Gantt dominates the
          viewport. Sticky top + left cells stay visible while scanning
          50+ rows or multi-month ranges. */}
      <div className={`tactical-card overflow-auto ${fullscreen ? "flex-1 min-h-0" : "h-[80vh]"}`}>
        <div className="min-w-full">
          {/* Header row */}
          <div className="flex sticky top-0 bg-[var(--bg-1)] z-10 border-b border-[var(--border)]">
            <div className={`${fullscreen ? "w-52" : "w-40"} flex-shrink-0 px-3 py-2 sticky left-0 bg-[var(--bg-1)] z-20 ${fullscreen ? "text-xs" : "text-[10px]"} uppercase tracking-widest text-zinc-500 border-r border-[var(--border)]`}>Loom</div>
            <div className="flex" style={{ width: totalWidth }}>
              {days.map((d, i) => {
                const dow = new Date(d).getDay();
                const isWeekend = dow === 0 || dow === 6;
                return (
                  <div key={d} className={`border-r border-[var(--border)]/50 py-2 text-center font-mono ${fullscreen ? "text-xs" : "text-[10px]"} ${isWeekend ? "text-amber-400/50" : "text-zinc-500"}`} style={{ width: CELL_W }}>
                    {d.slice(5)}
                  </div>
                );
              })}
            </div>
          </div>
          {/* Data rows */}
          {rowsMachines.map((m, mIdx) => {
            const machinePlans = plans.filter(p => p.machine_id === m.id);
            // Cross-machine drop-target highlight: while a bar is being dragged,
            // resolve the projected target row and light it up so planners
            // see WHERE the bar will land before they release.
            const dragging = dragState && dragState.hasMoved;
            let isDropTarget = false;
            if (dragging) {
              const dp = plans.find(x => x.id === dragState.planId);
              if (dp) {
                const curIdx = rowsMachines.findIndex(rm => rm.id === dp.machine_id);
                const tgt = Math.max(0, Math.min(rowsMachines.length - 1, curIdx + (dragState.dyRows || 0)));
                if (tgt === mIdx && tgt !== curIdx) isDropTarget = true;
              }
            }
            // Feb 2026 · Multi-SO Machine Stacking · flash the drop-target row
            // red when the dragged bar's base article clashes with an existing
            // active plan on this loom (backend would reject the release).
            const isIncompatibleTarget = isDropTarget && dropTargetIncompatible && dropTargetIncompatible.machineId === m.id;
            const isSuggestPulse = pulseMachineId === m.id;
            return (
              <div key={m.id} className={`flex border-b border-[var(--border)] transition-colors ${isIncompatibleTarget ? "bg-red-500/15 animate-pulse" : isDropTarget ? "bg-blue-500/10" : isSuggestPulse ? "bg-emerald-500/10 animate-pulse" : ""}`} style={{ height: ROW_H }} data-testid={`gantt-row-${m.code}`}>
                <div className={`${fullscreen ? "w-52" : "w-40"} flex-shrink-0 px-3 py-1 sticky left-0 z-10 border-r border-[var(--border)] transition-colors overflow-hidden ${isIncompatibleTarget ? "bg-red-500/25" : isDropTarget ? "bg-blue-500/20" : isSuggestPulse ? "bg-emerald-500/20" : "bg-[var(--bg-1)]"}`}>
                  <div className="font-mono font-semibold text-white text-xs flex items-center gap-1.5 leading-tight">
                    <input
                      type="checkbox"
                      data-testid={`gantt-loom-visible-${m.code}`}
                      checked={!hiddenLooms.has(m.id)}
                      onChange={() => toggleLoomHidden(m.id)}
                      title="Uncheck to hide this loom row"
                      className="w-3 h-3 accent-emerald-500 cursor-pointer flex-shrink-0"
                    />
                    {m.code}
                    {canLock && (() => {
                      const locked = machineHasActiveLock(m.id);
                      const auto = machineHasAutoLock(m.id);
                      const nudgeable = plans.some(pp => pp.machine_id === m.id && pp.status === "Planned" && !pp.locked);
                      return (
                        <>
                          {nudgeable && (
                            <button
                              onClick={(e) => { e.stopPropagation(); pullForwardMachine(m); }}
                              data-testid={`gantt-machine-pull-${m.code}`}
                              title="Pull every unlocked Planned bar on this loom forward to close gaps left by early completions"
                              className="ml-auto flex items-center gap-1 px-1.5 h-5 rounded-sm text-[9px] font-bold uppercase tracking-widest bg-[var(--bg-0)] border border-blue-400/40 text-blue-200 hover:border-blue-400 hover:bg-blue-500/15"
                            >⇤ PULL</button>
                          )}
                          <button
                            onClick={(e) => { e.stopPropagation(); toggleMachineLock(m); }}
                            className={`${nudgeable ? "" : "ml-auto"} flex items-center gap-1 px-1.5 h-5 rounded-sm text-[9px] font-bold uppercase tracking-widest transition-colors ${locked ? (auto ? "bg-red-500/25 border border-red-400/60 text-red-200 hover:bg-red-500/40" : "bg-amber-500/25 border border-amber-400/60 text-amber-200 hover:bg-amber-500/40") : "bg-[var(--bg-0)] border border-[var(--border)] text-zinc-400 hover:border-emerald-400/50 hover:text-emerald-200"}`}
                            data-testid={`gantt-machine-lock-${m.code}`}
                            title={locked ? (auto ? "Row auto-locked · Admin override required to unlock" : "Row manually locked · click to unlock") : "Lock this row so no bar can be dragged/extended"}
                          >
                            {locked ? <LockKeyIcon size={10} weight="fill" /> : <LockKeyOpenIcon size={10} />}
                            <span>{locked ? (auto ? "RUN" : "LOCK") : "LOCK"}</span>
                          </button>
                        </>
                      );
                    })()}
                    {isIncompatibleTarget && (
                      <span
                        className="text-[9px] px-1 rounded-sm bg-red-500/50 border border-red-300/60 text-red-50 font-bold uppercase tracking-widest"
                        data-testid={`gantt-drop-incompatible-${m.code}`}
                        title={`Cannot stack · this loom holds Article ${dropTargetIncompatible.theirArticle} (${dropTargetIncompatible.clashOrderNo}), you're dragging Article ${dropTargetIncompatible.ourArticle}. Backend will reject.`}
                      >⚠ INCOMPATIBLE</span>
                    )}
                    {isDropTarget && !isIncompatibleTarget && <span className="text-[9px] px-1 rounded-sm bg-blue-500/40 text-blue-100" data-testid={`gantt-drop-target-${m.code}`}>DROP HERE</span>}
                    {isSuggestPulse && !isDropTarget && <span className="text-[9px] px-1 rounded-sm bg-emerald-500/40 text-emerald-100" data-testid={`gantt-fastest-${m.code}`}>FASTEST</span>}
                  </div>
                  {/* Sep 03, 2026 · Model + type + status collapsed onto a
                      single truncated line so the tighter ROW_H doesn't
                      clip. Model still leads for identification; falls
                      back to type when absent. */}
                  <div className="text-[10px] text-zinc-500 truncate font-mono leading-tight" data-testid={`gantt-meta-${m.code}`}
                    title={`${m.machine_model || m.machine_type} · ${m.status}`}>
                    {m.machine_model
                      ? <><span className="text-zinc-300">{m.machine_model}</span> · {m.status}</>
                      : <>{m.machine_type} · {m.status}</>}
                  </div>
                </div>
                <div className="relative" style={{ width: totalWidth, height: ROW_H }}>
                  {/* Weekend shading + grid */}
                  {days.map((d, i) => {
                    const dow = new Date(d).getDay();
                    const isWeekend = dow === 0 || dow === 6;
                    return <div key={d} className={`absolute top-0 border-r border-[var(--border)]/30 ${isWeekend ? "bg-[var(--bg-2)]/30" : ""}`} style={{ left: i * CELL_W, width: CELL_W, height: ROW_H }} />;
                  })}
                  {/* Bars — split into changeover · setup · production segments and
                      overlay a green progress fill on the production segment so a
                      planner can see live completion at a glance. Delayed plans
                      (today past planned_finish, still open) get a red pulse ring. */}
                  {machinePlans.map(p => {
                    const pos = barFor(p);
                    if (!pos) return null;
                    const c = +(p.changeover_hours || 0);
                    const s = +(p.setup_hours || 0);
                    const pr = +(p.production_hours || 0);
                    const total = c + s + pr;
                    const hasPhases = total > 0;
                    const planned = +(p.planned_qty || 0);
                    const actual = +(p.actual_qty || 0);
                    const progress = planned > 0 ? Math.min(100, (actual / planned) * 100) : 0;
                    const today = new Date().toISOString().slice(0, 10);
                    const isOverdue = p.status !== "Completed" && p.planned_finish && p.planned_finish < today;
                    const onTime = p.status === "Completed" && p.planned_finish >= today;
                    // Delivery risk (Feb 2026): open plan whose planned_finish
                    // lands AFTER the SO's promised delivery_date but hasn't
                    // gone overdue yet. Amber outline gives planners a
                    // heads-up before it becomes a red-alert overdue.
                    const promisedDelivery = promisedForPlan(p);
                    // Feb 2026 · Post-Loom Buffer = 4 days. The true weaving
                    // deadline is `delivery - 4d` so Finishing/Packing has
                    // room to breathe. All at-risk / on-track colouring on
                    // the Gantt is measured against this weaving cut-off.
                    const weavingDeadline = promisedDelivery
                      ? new Date(new Date(promisedDelivery).getTime() - 4 * 86400000).toISOString().slice(0, 10)
                      : "";
                    const lineCtx = lineContextForPlan(p);
                    const lineDeliveryHint = lineCtx && lineCtx.promised
                      ? `Line delivery · ${lineCtx.promised} (from ${lineCtx.source}${lineCtx.article ? ` · ${lineCtx.article}` : ""})`
                      : "";
                    const atRisk = !isOverdue && p.status !== "Completed" && weavingDeadline && p.planned_finish && p.planned_finish > weavingDeadline;
                    const riskDelta = atRisk ? Math.round((new Date(p.planned_finish) - new Date(weavingDeadline)) / 86400000) : 0;
                    // Feb 2026 · Schedule Status vs Line Delivery Date.
                    // Green ring when the plan will finish ON or BEFORE the
                    // per-line SO/ESO Delivery Date; Red is already handled
                    // by the atRisk/isOverdue rings. `daysOverdueVsDelivery`
                    // is the numeric days-late chip shown inline on the bar.
                    const onTrackVsDelivery = !!(!isOverdue && p.status !== "Completed" && weavingDeadline && p.planned_finish && p.planned_finish <= weavingDeadline);
                    const daysOverdueVsDelivery = atRisk ? riskDelta : 0;
                    const ci = chainIndex.get(p.id) || 0;
                    // Feb 2026 · Bar colour is now keyed on the SO/ESO id
                    // (via `orderColourIndex`) so one order = one hue across
                    // every loom + queue slot. `ci` still drives the "Queue #N"
                    // text badge below.
                    const colourSlot = orderColourIndex.get(p.id) ?? 0;
                    const palette = QUEUE_PALETTE[colourSlot % QUEUE_PALETTE.length];
                    const isPlanned = p.status === "Planned";
                    const isRunning = p.status === "Running";
                    // Sep 05, 2026 · Live Capacity Re-balancing — auto-finished
                    // bars carry `auto_finished_at` + optional `original_planned_finish`.
                    // Render them in a distinct solid dark-grey so planners spot the
                    // gap. Time-saved chip surfaces the days recovered.
                    const isFinished = p.status === "Completed" && p.auto_finished_at;
                    const savedDays = (() => {
                      const of = p.original_planned_finish; const pf = p.planned_finish;
                      if (!of || !pf) return 0;
                      const diff = (new Date(of).getTime() - new Date(pf).getTime()) / 86400000;
                      return diff > 0 ? Math.round(diff) : 0;
                    })();
                    const barBg = isFinished ? "bg-zinc-800" : isRunning ? "bg-emerald-900/40" : (p.status === "Completed" ? "bg-zinc-700/50" : palette.bar);
                    const fillBg = onTime ? "bg-emerald-500/70" : isOverdue ? "bg-red-500/70" : isRunning ? "bg-emerald-500/70" : palette.fill;
                    const queueBadge = isPlanned && ci > 0 ? palette.label : null;
                    // Head-split sliver stacking + drag transform.
                    const lay = shardLayout.get(p.id) || { yOffset: 0, heightPct: 1 };
                    const innerH = ROW_H - 12;
                    const barHeight = Math.max(6, Math.floor(innerH * lay.heightPct) - (lay.heightPct < 1 ? 1 : 0));
                    const barTop = 6 + Math.round(innerH * lay.yOffset);
                    const isShard = lay.heightPct < 1;
                    const isDragged = dragState && dragState.planId === p.id && dragState.hasMoved;
                    const dragDx = isDragged ? dragState.dxDays * CELL_W : 0;
                    const dragDy = isDragged ? (dragState.dyRows || 0) * ROW_H : 0;
                    const inConflict = overlapPlanIds.has(p.id);
                    // Feb 2026 · projected hours on the CURRENT loom (live,
                    // reflects any cross-machine reallocation instantly).
                    const item = items.find(it => it.id === p.item_id) || null;
                    const proj = projectedHoursOnMachine(p, m, item);
                    const projLine = proj
                      ? `Projected · ${(+p.planned_qty).toLocaleString("en-IN")}m ÷ ${proj.mPerHr} m/hr = ${proj.hours}h (${proj.days}d) on ${proj.heads || "?"}-head ${m.code}`
                      : "";
                    // Feb 2026 · lock state + hours-delta chip
                    const pinned = isPinnedPlan(p);
                    const deltaChip = hoursDeltaChips.get(p.id) || null;
                    return (
                      <div
                        key={p.id}
                        role="button"
                        tabIndex={0}
                        aria-label={`Plan ${woNoByPlan(p) || orderNo(p.order_id)} · ${productCodeForPlan(p) || ""} · ${p.planned_start} → ${p.planned_finish}`}
                        onMouseDown={(e) => { if (e.button !== 0) return; const pinnedNow = isPinnedPlan(p); setDragState({ planId: p.id, initialX: e.clientX, initialY: e.clientY, dxDays: 0, dyRows: 0, hasMoved: false, pinned: pinnedNow }); document.body.style.cursor = pinnedNow ? "not-allowed" : "grabbing"; }}
                        onClick={(e) => { if (dragState && dragState.planId === p.id && (dragState.hasMoved || dragState.releasing)) { e.preventDefault(); return; } setSelected({ ...p, machine: m }); }}
                        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setSelected({ ...p, machine: m }); } }}
                        onPointerEnter={(e) => {
                          const so = orders.find(o => o.id === p.order_id);
                          const oNo = so?.order_no || orderNo(p.order_id);
                          const isExport = (so?.kind === "export") || String(oNo || "").toUpperCase().startsWith("ESO");
                          // Feb 2026 · Split-heads siblings — when the hovered
                          // bar is a shard (heads_used > 0) find every other
                          // shard on the same loom + same start/finish window
                          // and surface all their product codes AND head counts
                          // in the tooltip so planners see the load split at a
                          // glance. Non-split plans get a single-entry list.
                          let items = [{ code: productCodeForPlan(p), heads: +p.heads_used || 0 }];
                          if (+p.heads_used > 0) {
                            const siblings = plans.filter(x =>
                              x.machine_id === p.machine_id &&
                              +x.heads_used > 0 &&
                              (x.planned_start || "") === (p.planned_start || "") &&
                              (x.planned_finish || "") === (p.planned_finish || "")
                            );
                            if (siblings.length > 1) {
                              const others = siblings
                                .filter(x => x.id !== p.id)
                                .sort((a, b) => (a.line_id || a.item_id || "").localeCompare(b.line_id || b.item_id || ""));
                              items = [
                                { code: productCodeForPlan(p), heads: +p.heads_used || 0 },
                                ...others.map(x => ({ code: productCodeForPlan(x), heads: +x.heads_used || 0 })),
                              ].filter(it => it.code);
                            }
                          }
                          setHoverTip({
                            plan: p,
                            orderNo: oNo,
                            productItems: items,
                            kind: isExport ? "EXP" : "DOM",
                            x: e.clientX,
                            y: e.clientY,
                          });
                        }}
                        onPointerMove={(e) => {
                          setHoverTip(prev => (prev && prev.plan && prev.plan.id === p.id ? { ...prev, x: e.clientX, y: e.clientY } : prev));
                        }}
                        onPointerLeave={() => {
                          setHoverTip(prev => (prev && prev.plan && prev.plan.id === p.id ? null : prev));
                        }}
                        data-testid={`gantt-bar-${p.id}`}
                        className={"absolute rounded-sm border " + (hasPhases ? ((isPlanned && ci > 0 ? palette.ring : "border-white/10") + " bg-transparent") : statusColour(p.status)) + (isOverdue ? " ring-2 ring-red-500/70 animate-pulse" : atRisk ? " ring-2 ring-amber-400/80" : onTrackVsDelivery ? " ring-1 ring-emerald-400/60" : "") + (pinned ? " ring-1 ring-amber-400/50" : "") + (inConflict ? " ring-2 ring-red-500 animate-pulse" : "") + (isDragged ? " opacity-70 shadow-lg z-30 cursor-grabbing ring-1 ring-white/60" : (pinned ? " cursor-not-allowed" : " cursor-grab")) + " hover:brightness-125 focus:outline-none focus:ring-1 focus:ring-white/40 text-white text-[10px] font-mono truncate text-left overflow-hidden transition-transform"}
                        style={{ left: pos.left + 2, width: pos.width - 4, top: barTop, height: barHeight, transform: (dragDx || dragDy) ? `translate(${dragDx}px, ${dragDy}px)` : undefined, transition: isDragged ? "transform 60ms linear" : "transform 220ms cubic-bezier(0.22, 1, 0.36, 1), left 220ms cubic-bezier(0.22, 1, 0.36, 1), width 220ms cubic-bezier(0.22, 1, 0.36, 1)", zIndex: isDragged ? 40 : 5 }}
                      >
                        {hasPhases ? (
                          <div className="flex h-full w-full">
                            {c > 0 && <div className="bg-yellow-500/70 border-r border-yellow-300/30" style={{ width: `${(c / total) * 100}%` }} title={`Changeover ${c}h`}></div>}
                            {s > 0 && <div className="bg-orange-500/70 border-r border-orange-300/30" style={{ width: `${(s / total) * 100}%` }} title={`Setup ${s}h`}></div>}
                            {pr > 0 && (
                              <div className={`relative ${barBg} flex-1 flex items-center px-1.5`} title={`Production ${pr}h · ${planned}m · actual ${actual}m (${progress.toFixed(0)}%)${queueBadge ? " · " + queueBadge : ""}${isShard ? " · " + (p.heads_used || "?") + "h shard" : ""}`}>
                                <div className={`absolute inset-y-0 left-0 ${fillBg} transition-all`} style={{ width: `${progress}%` }}></div>
                                <span className="relative z-10 truncate flex items-center gap-1">
                                  {pinned && <LockKeyIcon size={9} weight="fill" className="text-amber-300 flex-shrink-0" data-testid={`gantt-lock-${p.id}`} />}
                                  {canLock && !p.locked && !isFinished && (p.status === "Planned" || p.status === "Running") && (
                                    <button
                                      onClick={(e) => { e.stopPropagation(); togglePlanLock(p); }}
                                      onMouseDown={(e) => e.stopPropagation()}
                                      data-testid={`gantt-bar-lock-${p.id}`}
                                      title="Lock this bar (freeze drag / extend / cross-loom moves)"
                                      className="flex-shrink-0 opacity-50 hover:opacity-100 text-white/70 hover:text-amber-300 transition-opacity"
                                    ><LockKeyOpenIcon size={9} /></button>
                                  )}
                                  {p.locked && canLock && !isFinished && !(p.lock_source === "auto" && !isAdmin) && (
                                    <button
                                      onClick={(e) => { e.stopPropagation(); togglePlanLock(p); }}
                                      onMouseDown={(e) => e.stopPropagation()}
                                      data-testid={`gantt-bar-unlock-${p.id}`}
                                      title={p.lock_source === "auto" ? "Admin override · release auto-lock" : "Release manual lock"}
                                      className="flex-shrink-0 text-amber-300 hover:text-white transition-colors"
                                    ><LockKeyIcon size={9} weight="fill" /></button>
                                  )}
                                  {isFinished && (
                                    <span
                                      data-testid={`gantt-finished-badge-${p.id}`}
                                      title={`Auto-finished · snapped to ${p.planned_finish}${p.original_planned_finish ? ` (was ${p.original_planned_finish})` : ""} · freed capacity for downstream bars`}
                                      className="text-[9px] font-bold px-1 rounded-sm bg-zinc-500/70 border border-zinc-300/60 text-white uppercase tracking-widest flex-shrink-0"
                                    >FINISHED{savedDays > 0 ? ` · ${savedDays}d` : ""}</span>
                                  )}
                                  {p.locked && p.lock_source === "auto" && (
                                    <span
                                      data-testid={`gantt-running-badge-${p.id}`}
                                      title={`Auto-locked · ${p.locked_reason || "production entry recorded"} · Admin override required to reschedule`}
                                      className="text-[9px] font-bold px-1 rounded-sm bg-red-500/60 border border-red-300/60 text-white uppercase tracking-widest flex-shrink-0 animate-pulse"
                                    >RUNNING</span>
                                  )}
                                  {deltaChip && (
                                    <span
                                      data-testid={`gantt-hours-delta-${p.id}`}
                                      title={`Cross-loom saving · ${deltaChip.fromH}h → ${deltaChip.toH}h`}
                                      className="text-[9px] font-bold px-1 rounded-sm bg-blue-500/60 border border-blue-300/60 text-white animate-pulse flex-shrink-0"
                                    >
                                      {deltaChip.fromH}h→{deltaChip.toH}h
                                    </span>
                                  )}
                                  {queueBadge && <span data-testid={`gantt-queue-badge-${p.id}`} className="opacity-90 text-[9px] font-semibold px-1 rounded-sm bg-white/10">Q{ci + 1}</span>}
                                  {isShard && <span data-testid={`gantt-shard-badge-${p.id}`} className="opacity-90 text-[9px] font-semibold px-1 rounded-sm bg-purple-500/40 border border-purple-400/40">{p.heads_used || "?"}h</span>}
                                  {stackCountByPlan.has(p.id) && (
                                    <span
                                      data-testid={`gantt-stack-chip-${p.id}`}
                                      className="opacity-90 text-[9px] font-semibold px-1 rounded-sm bg-cyan-500/40 border border-cyan-400/50 text-cyan-50"
                                      title={`Stacked with ${stackCountByPlan.get(p.id) - 1} other same-article plan${stackCountByPlan.get(p.id) - 1 === 1 ? "" : "s"} on ${m.code}`}
                                    >▤{stackCountByPlan.get(p.id)}</span>
                                  )}
                                  {(barHeight >= 14 || isShard) && (
                                    <GanttBarLabel
                                      plan={p}
                                      woNo={woNoByPlan(p)}
                                      orderNo={orderNo(p.order_id)}
                                      productCode={productCodeForPlan(p)}
                                      progress={progress}
                                      onTrackVsDelivery={onTrackVsDelivery}
                                      atRisk={atRisk}
                                      isOverdue={isOverdue}
                                      promisedDelivery={weavingDeadline}
                                      customerDelivery={promisedDelivery}
                                      daysOverdueVsDelivery={daysOverdueVsDelivery}
                                      isShard={isShard}
                                      includeProgress={false}
                                    />
                                  )}
                                  {barHeight >= 14 && planned > 0 && (() => {
                                    const wq = woQtyByPlan(p);
                                    return (
                                      <span data-testid={`gantt-qty-${p.id}`} className="opacity-80 text-[9px] ml-1">
                                        · {(+planned).toLocaleString("en-US", { maximumFractionDigits: 0 })}m
                                        {wq > 0 && <span className="text-cyan-200/90" title={`WO cap: ${wq.toLocaleString("en-US")}m`}> / {wq.toLocaleString("en-US", { maximumFractionDigits: 0 })}m</span>}
                                      </span>
                                    );
                                  })()}
                                  {barHeight >= 14 && progress > 0 && <span className="opacity-80 text-[9px]"> · {progress.toFixed(0)}%</span>}
                                  {isOverdue && <span className="text-red-200">⚠</span>}
                                  {betterLoomHints.has(p.id) && (() => {
                                    const h = betterLoomHints.get(p.id);
                                    return <span data-testid={`gantt-better-loom-pip-${p.id}`} className="text-amber-300 font-bold" title={`Better loom available: ${h.targetCode} finishes ${h.targetFinish} · ${h.daysSaved}d faster. Open bar → Suggest Fastest Loom to move.`}>▲</span>;
                                  })()}
                                  {onTime && <span className="text-emerald-200">✓</span>}
                                </span>
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="px-2 py-1 relative">
                            <div className={`absolute inset-y-0 left-0 ${isOverdue ? "bg-red-500/40" : (isPlanned && ci > 0 ? palette.fill : "bg-emerald-500/40")}`} style={{ width: `${progress}%` }}></div>
                            <div className="truncate relative flex items-center gap-1">
                              {pinned && <LockKeyIcon size={9} weight="fill" className="text-amber-300" data-testid={`gantt-lock-${p.id}`} />}
                              {isFinished && (
                                <span
                                  data-testid={`gantt-finished-badge-${p.id}`}
                                  title={`Auto-finished · snapped to ${p.planned_finish}${p.original_planned_finish ? ` (was ${p.original_planned_finish})` : ""}`}
                                  className="text-[9px] font-bold px-1 rounded-sm bg-zinc-500/70 border border-zinc-300/60 text-white uppercase tracking-widest flex-shrink-0"
                                >FINISHED{savedDays > 0 ? ` · ${savedDays}d` : ""}</span>
                              )}
                              {p.locked && p.lock_source === "auto" && (
                                <span
                                  data-testid={`gantt-running-badge-${p.id}`}
                                  title={`Auto-locked · ${p.locked_reason || "production entry recorded"} · Admin override required to reschedule`}
                                  className="text-[9px] font-bold px-1 rounded-sm bg-red-500/60 border border-red-300/60 text-white uppercase tracking-widest flex-shrink-0 animate-pulse"
                                >RUNNING</span>
                              )}
                              {deltaChip && (
                                <span
                                  data-testid={`gantt-hours-delta-${p.id}`}
                                  className="text-[9px] font-bold px-1 rounded-sm bg-blue-500/60 border border-blue-300/60 text-white animate-pulse"
                                >
                                  {deltaChip.fromH}h→{deltaChip.toH}h
                                </span>
                              )}
                              {queueBadge && <span className="text-[9px] font-semibold px-1 rounded-sm bg-white/10 mr-1">Q{ci + 1}</span>}
                              {isShard && <span className="text-[9px] font-semibold px-1 rounded-sm bg-purple-500/40 mr-1">{p.heads_used || "?"}h</span>}
                              {stackCountByPlan.has(p.id) && (
                                <span
                                  data-testid={`gantt-stack-chip-${p.id}`}
                                  className="text-[9px] font-semibold px-1 rounded-sm bg-cyan-500/40 border border-cyan-400/50 text-cyan-50 mr-1"
                                  title={`Stacked with ${stackCountByPlan.get(p.id) - 1} other same-article plan${stackCountByPlan.get(p.id) - 1 === 1 ? "" : "s"} on ${m.code}`}
                                >▤{stackCountByPlan.get(p.id)}</span>
                              )}
                              <GanttBarLabel
                                plan={p}
                                woNo={woNoByPlan(p)}
                                orderNo={orderNo(p.order_id)}
                                productCode={productCodeForPlan(p)}
                                progress={progress}
                                onTrackVsDelivery={onTrackVsDelivery}
                                atRisk={atRisk}
                                isOverdue={isOverdue}
                                promisedDelivery={promisedDelivery}
                                daysOverdueVsDelivery={daysOverdueVsDelivery}
                                isShard={isShard}
                                includeProgress
                              />
                            </div>
                            <div className="truncate opacity-70 text-[9px] relative" data-testid={`gantt-qty-nolane-${p.id}`}>
                              {(+planned).toLocaleString("en-US", { maximumFractionDigits: 0 })} m
                              {(() => { const wq = woQtyByPlan(p); return wq > 0 ? <span className="text-cyan-200/90 ml-1" title={`WO cap: ${wq.toLocaleString("en-US")}m`}>/ {wq.toLocaleString("en-US", { maximumFractionDigits: 0 })} m</span> : null; })()}
                              {isOverdue && <span className="text-red-300 ml-1">⚠ overdue</span>}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
          {!loading && rowsMachines.length === 0 && (
            <div className="text-center py-16 text-zinc-500">No machines match the current filter.</div>
          )}
        </div>
      </div>

      </>)}
      {/* Feb 2026 · Estimated Delivery table now renders ONLY on its own
          dedicated tab (deliveryOnly variant). Removed from the Gantt Board
          bottom to declutter the loom calendar view. */}
      {deliveryOnly && deliveryEstimates.length > 0 && (
        <div className="tactical-card p-4" data-testid="gantt-delivery-table">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="overline text-[10px] mb-0.5">Estimated Delivery per SO + Product</div>
              <div className="text-xs text-zinc-500">Latest <span className="font-mono">planned_finish</span> across all shop-floor plans, compared with the SO's promised delivery date.</div>
            </div>
            <div className="flex items-center gap-3 text-[10px] uppercase tracking-widest">
              <span className="inline-flex items-center gap-1.5"><span className="inline-block w-2.5 h-2.5 rounded-sm bg-red-500/70"></span><span className="text-zinc-400 normal-case tracking-normal">Delayed</span><span className="font-mono text-red-300">{deliveryEstimates.filter(d => d.delayed).length}</span></span>
              <span className="inline-flex items-center gap-1.5"><span className="inline-block w-2.5 h-2.5 rounded-sm bg-amber-500/70"></span><span className="text-zinc-400 normal-case tracking-normal">Same-day</span><span className="font-mono text-amber-300">{deliveryEstimates.filter(d => d.delta_days === 0).length}</span></span>
              <span className="inline-flex items-center gap-1.5"><span className="inline-block w-2.5 h-2.5 rounded-sm bg-emerald-500/70"></span><span className="text-zinc-400 normal-case tracking-normal">On-time</span><span className="font-mono text-emerald-300">{deliveryEstimates.filter(d => d.on_time && d.delta_days !== 0).length}</span></span>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-zinc-500 uppercase tracking-widest text-[9px] border-b border-[var(--border)]/60">
                  <th className="text-left py-2 px-1.5 font-medium">Plan Doc No.</th>
                  <th className="text-left py-2 px-1.5 font-medium">Plan Date</th>
                  <th className="text-left py-2 px-1.5 font-medium">SO/ESO</th>
                  <th className="text-left py-2 px-1.5 font-medium">Customer</th>
                  <th className="text-left py-2 px-1.5 font-medium">WO</th>
                  <th className="text-left py-2 px-1.5 font-medium">Product Code</th>
                  <th className="text-right py-2 px-1.5 font-medium">WO Order Qty</th>
                  <th className="text-right py-2 px-1.5 font-medium" title="WO Production Qty = Order Qty + buffer">WO Production Qty</th>
                  <th className="text-right py-2 px-1.5 font-medium">Planned Qty</th>
                  <th className="text-right py-2 px-1.5 font-medium">Completed Qty</th>
                  <th className="text-right py-2 px-1.5 font-medium">Balance Qty</th>
                  <th className="text-right py-2 px-1.5 font-medium">Progress</th>
                  <th className="text-left py-2 px-1.5 font-medium">Promised</th>
                  <th className="text-left py-2 px-1.5 font-medium">Est. Delivery</th>
                  <th className="text-right py-2 px-1.5 font-medium">Δ Days</th>
                  <th className="text-left py-2 px-1.5 font-medium whitespace-nowrap">Status</th>
                </tr>
              </thead>
              <tbody>
                {deliveryEstimates.map((d, i) => {
                  const tone = d.all_done ? "text-zinc-400" : d.delayed ? "text-red-300" : d.delta_days === 0 ? "text-amber-300" : d.on_time ? "text-emerald-300" : "text-white";
                  const badge = d.all_done
                    ? { cls: "bg-zinc-500/15 text-zinc-300 border-zinc-500/40", label: "Completed" }
                    : d.delayed
                    ? { cls: "bg-red-500/15 text-red-300 border-red-500/50", label: "Delayed", detail: `${d.delta_days}d late` }
                    : d.delta_days === 0
                    ? { cls: "bg-amber-500/15 text-amber-300 border-amber-500/50", label: "Same-day" }
                    : d.on_time
                    ? { cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/50", label: "On-time", detail: `${Math.abs(d.delta_days)}d early` }
                    : { cls: "bg-zinc-500/15 text-zinc-400 border-zinc-500/40", label: "No promise" };
                  const deltaLabel = d.delta_days === null ? "—" : d.delta_days > 0 ? ("+" + d.delta_days) : d.delta_days === 0 ? "0" : String(d.delta_days);
                  return (
                    <tr key={`${d.order_id}-${d.item_id}-${i}`} data-testid={`gantt-delivery-row-${d.order_no}-${d.item_id.slice(-6)}`} className="border-b border-[var(--border)]/40 hover:bg-[var(--bg-2)]/40 transition-colors">
                      <td className="py-2 px-1.5 font-mono whitespace-nowrap" data-testid={`gantt-delivery-plan-no-${d.order_no}-${d.item_id.slice(-6)}`}>
                        {d.plan_no
                          ? <span className="text-white">{d.plan_no}</span>
                          : <span className="text-zinc-500 italic">Legacy / Not Assigned</span>}
                      </td>
                      <td className="py-2 px-1.5 font-mono text-zinc-400 whitespace-nowrap" data-testid={`gantt-delivery-plan-date-${d.order_no}-${d.item_id.slice(-6)}`}>
                        {d.plan_date || "—"}
                      </td>
                      <td className="py-2 px-1.5 font-mono font-semibold text-white" data-testid={`gantt-delivery-order-${d.order_no}-${d.item_id.slice(-6)}`}>
                        <span className="inline-flex items-start gap-1.5">
                          {d.is_export && (
                            <span
                              data-testid={`gantt-delivery-exp-chip-${d.order_no}`}
                              className="shrink-0 px-1 py-0.5 rounded-sm bg-[var(--accent)]/15 text-[var(--accent)] text-[9px] font-mono uppercase tracking-widest border border-[var(--accent)]/30"
                              title="Export Sales Order"
                            >EXP</span>
                          )}
                          {(() => {
                            // Max two lines: only break point is before the final sequence number.
                            const ref = String(d.order_no || "");
                            const cut = ref.lastIndexOf("-") + 1;
                            return (
                              <span title={ref}>
                                <span className="whitespace-nowrap">{ref.slice(0, cut)}</span><wbr /><span className="whitespace-nowrap">{ref.slice(cut)}</span>
                              </span>
                            );
                          })()}
                        </span>
                      </td>
                      <td className="py-2 px-1.5 text-zinc-400 max-w-[150px]" data-testid={`gantt-delivery-customer-${d.order_no}-${d.item_id.slice(-6)}`}>
                        <span tabIndex={0} title={d.customer_name || ""} aria-label={d.customer_name || ""} className="block truncate focus:whitespace-normal focus:break-words focus:outline-none focus:text-white">{d.customer_name || "—"}</span>
                      </td>
                      <td className="py-2 px-1.5 font-mono text-emerald-300 whitespace-nowrap">{d.wo_no || "—"}</td>
                      <td className="py-2 px-1.5 font-mono text-zinc-300 min-w-[200px] max-w-[260px]" data-testid={`gantt-delivery-product-${d.order_no}-${d.item_id.slice(-6)}`}>
                        <span tabIndex={0} title={itemLabel(d.item_id)} aria-label={itemLabel(d.item_id)} className="block whitespace-normal [overflow-wrap:anywhere] leading-snug line-clamp-2 focus:line-clamp-none focus:outline-none focus:text-white">{itemLabel(d.item_id)}</span>
                      </td>
                      <td className="py-2 px-1.5 text-right font-mono whitespace-nowrap text-blue-300" data-testid={`gantt-delivery-wo-order-qty-${d.order_no}-${d.item_id.slice(-6)}`}>{d.wo_order_qty === null ? "—" : Math.round(d.wo_order_qty).toLocaleString("en-IN")}</td>
                      <td className="py-2 px-1.5 text-right font-mono whitespace-nowrap text-cyan-300" data-testid={`gantt-delivery-wo-prod-qty-${d.order_no}-${d.item_id.slice(-6)}`}>{d.wo_production_qty === null ? "—" : Math.round(d.wo_production_qty).toLocaleString("en-IN")}</td>
                      <td className="py-2 px-1.5 text-right font-mono whitespace-nowrap text-zinc-200">{d.total_qty.toLocaleString("en-IN")}<span className="text-[9px] text-zinc-500 ml-1">m</span></td>
                      <td className="py-2 px-1.5 text-right font-mono whitespace-nowrap text-emerald-300" data-testid={`gantt-delivery-completed-${d.order_no}-${d.item_id.slice(-6)}`}>
                        {Number(d.actual_qty || 0).toLocaleString("en-IN")}<span className="text-[9px] text-zinc-500 ml-1">m</span>
                      </td>
                      <td className={`py-2 px-1.5 text-right font-mono whitespace-nowrap ${((d.total_qty - (d.actual_qty || 0)) <= 0) ? "text-zinc-400" : "text-amber-300"}`} data-testid={`gantt-delivery-balance-${d.order_no}-${d.item_id.slice(-6)}`}>
                        {Math.max(0, d.total_qty - (d.actual_qty || 0)).toLocaleString("en-IN")}<span className="text-[9px] text-zinc-500 ml-1">m</span>
                      </td>
                      <td className="py-2 px-1.5 text-right">
                        <div className="flex items-center justify-end gap-1.5">
                          <div className="w-12 h-1.5 bg-[var(--bg-0)] rounded-sm overflow-hidden">
                            <div className={`h-full ${d.all_done ? "bg-zinc-400" : d.delayed ? "bg-red-500" : "bg-emerald-500"}`} style={{ width: `${d.progress_pct}%` }}></div>
                          </div>
                          <span className="font-mono text-[10px] text-zinc-400 w-9 text-right">{d.progress_pct.toFixed(0)}%</span>
                        </div>
                      </td>
                      <td className="py-2 px-1.5 font-mono text-zinc-300">{d.promised_delivery || "—"}</td>
                      <td className={`py-2 px-1.5 font-mono ${tone}`}>{d.estimated_delivery || "—"}</td>
                      <td className={`py-2 px-1.5 text-right font-mono whitespace-nowrap font-semibold ${tone}`}>{deltaLabel}</td>
                      <td className="py-2 px-1.5 whitespace-nowrap" data-testid={`gantt-delivery-status-${d.order_no}-${d.item_id.slice(-6)}`}>
                        <span className={`inline-flex flex-col items-start text-[10px] font-semibold px-1.5 py-0.5 rounded-sm border leading-tight ${badge.cls}`}>
                          <span className="whitespace-nowrap">{badge.label}</span>
                          {badge.detail && <span className="whitespace-nowrap font-mono font-normal text-[9px] opacity-90">{badge.detail}</span>}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Bar detail dialog */}
      <GanttBarTooltip data={hoverTip} />
      <Dialog open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
        <DialogContent className="bg-[var(--bg-1)] border-[var(--border)] text-white max-w-lg" data-testid="gantt-bar-dialog" description="Gantt detail action — full breakdown for the selected production span.">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <span>Plan · {selected?.source_wo_no || orderNo(selected?.order_id)}{selected?.source_order_no ? <span className="text-zinc-500 text-xs font-normal ml-1">· {selected.source_order_no}</span> : null}</span>
              <StatusBadge status={selected?.status} />
            </DialogTitle>
          </DialogHeader>
          {selected && (
            <div className="space-y-3 text-xs">
              <div className="grid grid-cols-2 gap-3">
                <div><div className="overline text-[10px] mb-0.5">Machine</div><div className="font-mono">{selected.machine.code} · {selected.machine.machine_type}</div></div>
                <div><div className="overline text-[10px] mb-0.5">Item</div><div className="font-mono" data-testid="gantt-dialog-item">{itemLabel(selected.item_id, selected)}</div></div>
                <div><div className="overline text-[10px] mb-0.5">Planned Qty</div><div className="font-mono">{Number(selected.planned_qty || 0).toFixed(0)} m</div></div>
                <div><div className="overline text-[10px] mb-0.5">Daily Target</div><div className="font-mono">{selected.daily_target || "—"} m/day</div></div>
                <div><div className="overline text-[10px] mb-0.5">Planned Start</div><div className="font-mono">{selected.planned_start}</div></div>
                <div><div className="overline text-[10px] mb-0.5">Planned Finish</div><div className="font-mono">{selected.planned_finish}</div></div>
              </div>
              {/* Feb 2026 · Live projected-hours strip. Recomputed against
                  the currently-selected loom every render so any cross-loom
                  reallocation immediately shows the new hours. */}
              {(() => {
                const item = items.find(it => it.id === selected.item_id) || null;
                const pr = projectedHoursOnMachine(selected, selected.machine, item);
                if (!pr) return null;
                return (
                  <div className="border border-blue-500/30 rounded-sm p-2.5 bg-blue-500/5" data-testid="gantt-projected-hours">
                    <div className="overline text-blue-300 mb-1">Projected on {selected.machine.code}</div>
                    <div className="text-[11px] text-zinc-300 leading-relaxed">
                      <span className="font-mono text-white">{Number(selected.planned_qty || 0).toLocaleString("en-IN")}m</span>
                      <span className="text-zinc-500"> ÷ </span>
                      <span className="font-mono text-white">{pr.mPerHr} m/hr</span>
                      <span className="text-zinc-500"> = </span>
                      <span className="font-mono font-bold text-blue-300">{pr.hours}h</span>
                      <span className="text-zinc-500"> ({pr.days}d)</span>
                      {pr.heads > 0 && <span className="text-zinc-400"> · <span className="font-mono text-white">{pr.heads}-head</span></span>}
                    </div>
                    <div className="text-[10px] text-zinc-500 mt-0.5">Recomputed live from Machine Type heads × Product speed × picks/cm. A cross-loom move on the board updates this instantly.</div>
                  </div>
                );
              })()}
              {/* Suggest fastest loom (Feb 2026): scans every compatible loom
                  and ranks by earliest projected finish using each machine's
                  own capacity. Highlights the winner on the Gantt board. */}
              <div className="border border-emerald-500/30 rounded-sm p-2.5 bg-emerald-500/5" data-testid="gantt-suggest-fastest">
                <div className="flex items-center justify-between mb-1.5">
                  <div className="overline text-emerald-300">Suggest Fastest Loom</div>
                  <button
                    data-testid="gantt-suggest-fastest-btn"
                    onClick={() => runSuggestFastest(selected)}
                    className="text-[10px] px-2 py-1 rounded-sm border border-emerald-500/40 hover:bg-emerald-500/15 text-emerald-200 flex items-center gap-1.5"
                    title="Scan every compatible loom and rank by earliest finish date"
                  ><SparkleIcon size={11} weight="bold" />Scan</button>
                </div>
                {fastestPlanId !== selected.id ? (
                  <div className="text-[10px] text-zinc-500 italic">Click <span className="font-mono text-emerald-300">Scan</span> to compare finish dates across all compatible looms.</div>
                ) : fastestList.length === 0 ? (
                  <div className="text-[10px] text-amber-300">No faster compatible loom found — the current loom is already the best fit or all alternates conflict.</div>
                ) : (
                  <ul className="space-y-1">
                    {fastestList.map((r, i) => {
                      const isWinner = i === 0;
                      const cur = selected.planned_finish || "";
                      const delta = (r.finish && cur) ? Math.round((new Date(r.finish) - new Date(cur)) / 86400000) : 0;
                      const deltaLabel = delta === 0 ? "same" : delta < 0 ? `${delta}d faster` : `+${delta}d slower`;
                      const deltaTone = delta < 0 ? "text-emerald-300" : delta > 0 ? "text-amber-300" : "text-zinc-400";
                      return (
                        <li key={r.machine.id} data-testid={`gantt-fastest-row-${r.machine.code}`} className={`flex items-center gap-2 p-1.5 rounded-sm text-[11px] border ${isWinner ? "border-emerald-500/40 bg-emerald-500/10" : "border-[var(--border)]/50 bg-[var(--bg-2)]/30"}`}>
                          {isWinner && <span className="text-[9px] px-1 rounded-sm bg-emerald-500/40 text-emerald-100 font-semibold">#1</span>}
                          <span className="font-mono text-white flex-1 truncate">
                            {r.machine.code} <span className="text-zinc-500">· {r.heads > 0 ? `${r.heads}h · ` : ""}{r.machine.machine_type}</span>
                            {r.productAware && <span className="ml-1 text-[9px] text-emerald-400" title="Ranked using picks/cm × speed × heads from Product Master">✓ product-aware</span>}
                          </span>
                          <span className="font-mono text-zinc-400 text-[10px]" title={`${r.daily.toLocaleString("en-IN")} m/day`}>{r.daily.toLocaleString("en-IN")} m/d</span>
                          <span className="font-mono text-zinc-300">finish {r.finish}</span>
                          <span className={`font-mono ${deltaTone}`}>{deltaLabel}</span>
                          <button
                            data-testid={`gantt-fastest-apply-${r.machine.code}`}
                            onClick={() => applyFastestMove(selected, r.machine)}
                            className="text-[10px] px-2 py-0.5 rounded-sm border border-emerald-500/40 hover:bg-emerald-500/20 text-emerald-200"
                          >Move here</button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
              {/* Progress + delivery-status indicator */}
              {(() => {
                const planned = +(selected.planned_qty || 0);
                const actual = +(selected.actual_qty || 0);
                const pct = planned > 0 ? Math.min(100, (actual / planned) * 100) : 0;
                const today = new Date().toISOString().slice(0, 10);
                const isOverdue = selected.status !== "Completed" && selected.planned_finish && selected.planned_finish < today;
                const onTime = selected.status === "Completed" && selected.planned_finish >= today;
                const isRunning = selected.status === "Running";
                const label = isOverdue ? "OVERDUE" : onTime ? "COMPLETED ON TIME" : isRunning ? "ON TRACK" : "PLANNED";
                const tone = isOverdue ? "border-red-500/50 bg-red-500/10 text-red-200" : onTime ? "border-emerald-500/50 bg-emerald-500/10 text-emerald-200" : isRunning ? "border-emerald-500/40 bg-emerald-500/5 text-emerald-100" : "border-[var(--border)] bg-[var(--bg-2)]/40 text-zinc-300";
                const fillTone = isOverdue ? "bg-red-500/70" : "bg-emerald-500/70";
                return (
                  <div data-testid="gantt-progress-card" className={`border rounded-sm p-2.5 ${tone}`}>
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="overline">Delivery Progress</div>
                      <div className="text-[10px] font-bold uppercase tracking-widest">{label}</div>
                    </div>
                    <div className="h-2.5 rounded-sm bg-[var(--bg-0)] border border-[var(--border)] overflow-hidden">
                      <div className={`h-full ${fillTone} transition-all`} style={{ width: `${pct}%` }}></div>
                    </div>
                    <div className="flex items-center justify-between mt-1.5 text-[10px] font-mono">
                      <span>{actual.toFixed(0)} / {planned.toFixed(0)} m</span>
                      <span data-testid="gantt-progress-pct" className="font-semibold">{pct.toFixed(1)}%</span>
                    </div>
                    {isOverdue && <div className="mt-1.5 text-[10px]">⚠ Finish date <span className="font-mono">{selected.planned_finish}</span> already passed — reschedule or accelerate.</div>}
                    {onTime && <div className="mt-1.5 text-[10px]">✓ Completed on or before planned finish {selected.planned_finish}.</div>}
                  </div>
                );
              })()}

              {/* Time-phase breakdown — populated when the plan was frozen via the
                  Planning Calculator; legacy rows without hours simply hide this. */}
              {((+selected.changeover_hours || 0) + (+selected.setup_hours || 0) + (+selected.production_hours || 0)) > 0 && (
                <div className="border border-[var(--border)]/60 rounded-sm p-2.5" data-testid="gantt-phase-breakdown">
                  <div className="overline mb-2">Time Phases (hours)</div>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="flex items-center gap-2">
                      <span className="inline-block w-3 h-3 rounded-sm bg-yellow-500/70"></span>
                      <div><div className="text-[10px] text-zinc-500 uppercase">Changeover</div><div className="font-mono text-white">{+selected.changeover_hours || 0}h</div></div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="inline-block w-3 h-3 rounded-sm bg-orange-500/70"></span>
                      <div><div className="text-[10px] text-zinc-500 uppercase">Setup</div><div className="font-mono text-white">{+selected.setup_hours || 0}h</div></div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="inline-block w-3 h-3 rounded-sm bg-blue-500/70"></span>
                      <div><div className="text-[10px] text-zinc-500 uppercase">Production</div><div className="font-mono text-white">{+selected.production_hours || 0}h</div></div>
                    </div>
                  </div>
                  <div className="mt-2 pt-2 border-t border-[var(--border)]/60 text-[10px] text-zinc-500">
                    Total <span className="text-white font-mono">{((+selected.changeover_hours || 0) + (+selected.setup_hours || 0) + (+selected.production_hours || 0)).toFixed(2)}h</span>
                  </div>
                </div>
              )}
              {selected.original_planned_qty && (
                <div className="border border-amber-500/40 bg-amber-500/10 rounded-sm px-3 py-2 text-[11px]">
                  <span className="overline text-amber-400 text-[10px]">Amended</span>
                  <div className="mt-1 text-zinc-300">Original planned qty was <span className="font-mono">{selected.original_planned_qty}m</span>.</div>
                </div>
              )}

              {/* Plan-history timeline (Feb 2026): chronological audit log of
                  Creates / Rescheduled drags / Add-qty / Release-qty / Amend
                  events. Reads `selected.history[]` (populated server-side by
                  every write endpoint). Reversed so newest is on top. */}
              <div className="border border-[var(--border)]/60 rounded-sm p-2.5" data-testid="gantt-history-timeline">
                <div className="flex items-center justify-between mb-2">
                  <div className="overline">Plan History</div>
                  <div className="flex items-center gap-2">
                    {/* Trace PDF (Feb 2026): opens the full customer-dispute report
                        for this plan's line in a new tab. Disabled + tooltip for
                        legacy plans that pre-date the stable-line-id migration. */}
                    <button
                      onClick={() => openTracePdf(selected.line_id, orderNo(selected.order_id))}
                      disabled={!selected.line_id}
                      data-testid="gantt-trace-pdf"
                      title={selected.line_id ? "Open the line-trace PDF in a new tab" : "This legacy plan has no line_id — resave the SO to back-fill"}
                      className="text-[10px] px-2 py-1 rounded-sm border border-[var(--border)] hover:bg-blue-500/10 text-blue-400 disabled:opacity-30 disabled:cursor-not-allowed flex items-center gap-1"
                    ><FileTextIcon size={11} weight="bold" />Trace PDF</button>
                    <div className="text-[10px] text-zinc-500 font-mono">{(selected.history || []).length} event{(selected.history || []).length === 1 ? "" : "s"}</div>
                  </div>
                </div>
                {!(selected.history || []).length ? (
                  <div className="text-[11px] text-zinc-500 italic">No history yet — legacy plan or freshly created without audit events.</div>
                ) : (
                  <ol className="relative border-l border-[var(--border)]/70 ml-1.5 space-y-3">
                    {[...selected.history].reverse().map((h, idx) => {
                      const action = (h.action || "").toLowerCase();
                      const dotCls =
                        action === "created" ? "bg-blue-400" :
                        action === "rescheduled" ? "bg-cyan-400" :
                        action === "added" ? "bg-emerald-400" :
                        action === "released" ? "bg-amber-400" :
                        action === "amended" ? "bg-purple-400" :
                        action === "modified" ? "bg-fuchsia-400" :
                        action.startsWith("status") ? "bg-teal-400" :
                        "bg-zinc-400";
                      const ts = h.at ? new Date(h.at) : null;
                      const tsLabel = ts && !isNaN(ts.getTime())
                        ? `${ts.toISOString().slice(0, 10)} ${ts.toISOString().slice(11, 16)} UTC`
                        : (h.at || "—");
                      const d = h.details || {};
                      let detailNode = null;
                      if (action === "created") {
                        detailNode = <span>Planned <span className="font-mono text-white">{Number(d.planned_qty || 0).toFixed(0)}m</span> from <span className="font-mono">{d.planned_start}</span> → <span className="font-mono">{d.planned_finish}</span>{d.heads_used ? <> · <span className="font-mono">{d.heads_used}h</span> shard</> : null}</span>;
                      } else if (action === "added") {
                        detailNode = <span>+<span className="font-mono text-emerald-300">{Number(d.add_qty || 0).toFixed(0)}m</span> ({Number(d.from_qty || 0).toFixed(0)} → <span className="font-mono">{Number(d.to_qty || 0).toFixed(0)}m</span>) · new finish <span className="font-mono">{d.new_finish}</span></span>;
                      } else if (action === "released") {
                        detailNode = <span>−<span className="font-mono text-amber-300">{Number(d.release_qty || 0).toFixed(0)}m</span> ({Number(d.from_qty || 0).toFixed(0)} → <span className="font-mono">{Number(d.to_qty || 0).toFixed(0)}m</span>) · new finish <span className="font-mono">{d.new_finish}</span></span>;
                      } else if (Array.isArray(d.changes) && d.changes.length) {
                        detailNode = (
                          <ul className="space-y-0.5">
                            {d.changes.map((c, i) => (
                              <li key={i}>
                                <span className="text-zinc-400">{c.field}:</span>{" "}
                                <span className="font-mono text-zinc-500 line-through">{String(c.from ?? "—")}</span>
                                <span className="mx-1 text-zinc-500">→</span>
                                <span className="font-mono text-white">{String(c.to ?? "—")}</span>
                              </li>
                            ))}
                            {d.reason && <li className="text-zinc-500 italic">Reason: {d.reason}</li>}
                          </ul>
                        );
                      }
                      return (
                        <li key={idx} className="ml-3" data-testid={`gantt-history-entry-${idx}`}>
                          <span className={`absolute -left-[5px] w-2.5 h-2.5 rounded-full ring-2 ring-[var(--bg-1)] ${dotCls}`}></span>
                          <div className="flex items-baseline justify-between gap-2">
                            <div className="text-[11px] font-semibold text-white">{h.action || "Event"}</div>
                            <div className="text-[9px] font-mono text-zinc-500 whitespace-nowrap">{tsLabel}</div>
                          </div>
                          <div className="text-[10px] text-zinc-400 mt-0.5">by <span className="text-zinc-200 font-mono">{h.by || "system"}</span></div>
                          {detailNode && <div className="text-[10px] text-zinc-300 mt-1">{detailNode}</div>}
                        </li>
                      );
                    })}
                  </ol>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            {selected && selected.auto_finished_at && (
              <button
                onClick={() => undoSnap(selected)}
                data-testid="gantt-dialog-undo-snap"
                title={`Restore original planned_finish${selected.original_planned_finish ? ` (${selected.original_planned_finish})` : ""} and re-open capacity for this plan. Use when a Production Entry was recorded against the wrong plan.`}
                className="px-3 py-2 rounded-sm text-xs font-semibold flex items-center gap-1.5 bg-[var(--bg-0)] border border-orange-400/60 text-orange-200 hover:bg-orange-500/15"
              >↺ Undo Snap</button>
            )}
            {selected && selected.status === "Planned" && !selected.locked && (
              <button
                onClick={() => pullForwardPlan(selected)}
                data-testid="gantt-dialog-pull-forward"
                title="Pull this Planned bar to the earliest available slot on its loom (closes gaps from early completions)"
                className="px-3 py-2 rounded-sm text-xs font-semibold flex items-center gap-1.5 bg-[var(--bg-0)] border border-blue-400/50 text-blue-200 hover:bg-blue-500/15"
              >⇤ Pull Forward</button>
            )}
            {selected && (
              <button
                data-testid="gantt-dialog-release"
                onClick={async () => {
                  const label = selected.machine?.code || "machine";
                  if (!window.confirm(`Release this plan from ${label} and return the WO to the unallocated queue?`)) return;
                  try {
                    const { data } = await api.post(`/production-plans/${selected.id}/release`);
                    toast.success(`Released ${Number(data.planned_qty || 0).toLocaleString("en-IN")}m from ${label}`);
                    setSelected(null);
                    // Force a fresh Gantt fetch — plans hook watches [from, to].
                    setFrom(f => f);
                    const refreshed = await api.get("/production-plans/");
                    setPlans(refreshed.data || []);
                  } catch (e) { toast.error(e?.response?.data?.detail || "Release failed"); }
                }}
                title="Release to Queue — unallocate this machine reservation (Admin / Production Manager only). Blocks if live production entries exist."
                className="px-3 py-2 rounded-sm text-xs font-semibold flex items-center gap-1.5 bg-[var(--bg-0)] border border-amber-400/60 text-amber-200 hover:bg-amber-500/15"
              >↺ Release to Queue</button>
            )}
            {selected && (canLock || (selected.locked && selected.lock_source === "auto" && !isAdmin)) && (
              <button
                onClick={() => togglePlanLock(selected)}
                data-testid="gantt-dialog-lock-toggle"
                disabled={selected.locked && selected.lock_source === "auto" && !isAdmin}
                title={selected.locked ? (selected.lock_source === "auto" ? (isAdmin ? "Admin override: release this auto-locked (In-Process) plan" : "Auto-locked · only an Administrator can unlock") : "Release the manual lock on this plan") : "Manually lock this plan to freeze drag / extend / cross-loom"}
                className={`mr-auto px-3 py-2 rounded-sm text-xs font-semibold flex items-center gap-1.5 ${selected.locked ? (selected.lock_source === "auto" ? "bg-red-500/20 border border-red-400/60 text-red-200 hover:bg-red-500/30 disabled:opacity-40 disabled:cursor-not-allowed" : "bg-amber-500/20 border border-amber-400/60 text-amber-200 hover:bg-amber-500/30") : "bg-[var(--bg-0)] border border-[var(--border)] text-zinc-300 hover:border-emerald-400/60 hover:text-emerald-200"}`}
              >
                {selected.locked ? <LockKeyIcon size={12} weight="fill" /> : <LockKeyOpenIcon size={12} />}
                {selected.locked ? (selected.lock_source === "auto" ? (isAdmin ? "Admin Override · Unlock" : "Auto-Locked · Admin Only") : "Unlock Plan") : "Lock Plan"}
              </button>
            )}
            <button onClick={() => setSelected(null)} className="px-4 py-2 rounded-sm bg-[var(--accent)] text-sm font-semibold text-white">Close</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
